import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { CalendarDays, CheckCircle2, ChevronLeft, ExternalLink, FileDown, FlaskConical, Info, RefreshCw, Send, Trash2, TriangleAlert, X } from 'lucide';
import { api } from '../api.js';
import { auth } from '../auth.js';
import logoUrl from '../assets/logo-full.png';
import { labPresentation, atmisOrderUrl, heeftCertificaat, labStatusPresentatie, monstertypeKeuzes } from '../labs.js';
import { FASES, faseVan, isAfgekeurd } from '../utils/lab-fase.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import './searchable-select.js';
import { lucideIcon } from '../icon.js';
import { monsterVerpakkingLabel, certificateCode, normalizeCertificateFormat,
         CERTIFICATE_FORMATS, DEFAULT_CERTIFICATE_FORMAT } from '@mv/models';
import { formatDate, formatDateTime, formatTime, parseTimestamp } from '../utils/datetime.js';
import { buildLabopdrachtDocument, diepteBereik } from '../utils/labopdracht-document.js';
import { uitslagTabel, meetwaardeTekst, onderGrens } from '../utils/lab-uitslag.js';
import base from '../styles/base.css?inline';
import './lab-mengmonsters.js';
import './lab-pakketten.js';
import './lab-assistent.js';
import './lab-certificaat.js';

/**
 * lab-order-page — één labopdracht, als eigen pagina op /labopdrachten/:id.
 *
 * Stond eerst als detailvenster ín het projecttabblad, met de projectkop en de projecttabs
 * eromheen. Je keek dan naar twee menu's tegelijk terwijl je met één opdracht bezig was, en
 * de labopdracht had geen eigen adres om te delen.
 *
 * De kop toont daarom alleen wat hier telt: het nummer van de opdracht, en daarnaast het
 * project als aanklikbare tegel -- dat is waar je heen wilt als je context zoekt.
 *
 * De pijl terug gaat naar waar je vandaan kwam. Kwam je van de bedrijfsbrede lijst, dan staat
 * dat in de URL (?terug=alle); anders is het projecttabblad de logische plek, ook als iemand
 * de link geplakt heeft gekregen.
 */
export class LabOrderPage extends LitElement {
  static properties = {
    orderId: { type: String },
    /** 'alle' als je via de hoofdpagina binnenkwam; anders het projecttabblad. */
    terug: { type: String },
    _loading: { state: true },
    _error: { state: true },
    _order: { state: true },
    _project: { state: true },
    _borings: { state: true },
    _connections: { state: true },
    _members: { state: true },
    _form: { state: true },
    _step: { state: true },
    _saving: { state: true },
    _mengmonsters: { state: true },   // wat het paneel toont, afgeleid van de analysemonsters
    _bewaarstand: { state: true },    // '' | 'bezig' | 'klaar' | 'fout'
    _adminstand: { state: true },     // idem, voor de administratieve data
    _verstuurOpen: { state: true },   // de bevestiging staat open
    _ontbreekt: { state: true },      // wat de controle vond
    _controleOpen: { state: true },   // de uitslag van de controle staat open
    _controleBezig: { state: true },
    _verstuurfout: { state: true },
    _labfouten: { state: true },
    _pdfBezig: { state: true },
    _bedrijfslogo: { state: true },
    _verzondenTab: { state: true },   // 'resultaten' | 'opdracht', zodra het lab heeft gerapporteerd
    _uitslag: { state: true },        // het resultaatbestand van het lab, uitgepakt
    _uitslagBezig: { state: true },
    _uitslagFout: { state: true },
    _verversBezig: { state: true },   // de stand bij het lab wordt opnieuw opgehaald
    _verversOpSlot: { state: true },
  };

  constructor() {
    super();
    this._loading = true;
    this._error = null;
    this._order = null;
    this._project = null;
    this._borings = [];
    this._connections = [];
    this._members = [];
    this._form = {};
    this._step = 'admin';
    this._saving = false;
    this._mengmonsters = [];
    this._bewaarstand = '';
    this._bewaarTimer = null;
    this._adminstand = '';
    this._adminTimer = null;
    this._verstuurOpen = false;
    this._ontbreekt = [];
    this._controleOpen = false;
    this._controleBezig = false;
    this._verstuurfout = '';
    this._labfouten = [];
    this._pdfBezig = false;
    this._bedrijfslogo = '';
    this._verzondenTab = 'opdracht';
    this._uitslag = null;
    this._uitslagBezig = false;
    this._uitslagFout = '';
    this._verversBezig = false;
    this._verversOpSlot = false;
  }

  disconnectedCallback() {
    clearTimeout(this._slotTimer);
    // Wat nog op bewaren wacht, gaat alsnog mee: wegnavigeren mag geen werk kosten.
    if (this._bewaarTimer) { clearTimeout(this._bewaarTimer); this._bewaarMengmonsters(); }
    if (this._adminTimer) { clearTimeout(this._adminTimer); this._bewaarAdmin(); }
    super.disconnectedCallback();
  }

  updated(changed) {
    if (!changed.has('orderId')) return;
    if (this.orderId) this._load();
    else this._loading = false;   // geen id in de URL: melden in plaats van blijven laden
  }

  /**
   * De opdracht en alles eromheen. Het project en zijn boringen komen hier vandaan en niet
   * van een bovenliggende pagina: deze pagina staat op zichzelf en moet het ook doen als je
   * er met een geplakte link binnenkomt.
   */
  async _load() {
    this._loading = true;
    this._error = null;
    try {
      const res = await api.getLabOrder(this.orderId);
      this._order = res.labOrder;
      this._vulFormulier();
      // Uit het overzicht: een concept opent op de stap die daar als volgende stond.
      const stap = new URLSearchParams(window.location.search).get('stap');
      if (!this._vergrendeld && ['admin', 'composition', 'packages'].includes(stap)) this._step = stap;
      const projectId = this._order?.projectId;
      const [connections, project, borings] = await Promise.all([
        api.getActiveLabConnections(),
        projectId ? api.getProject(projectId).catch(() => null) : null,
        projectId ? api.getProjectBorings(projectId).catch(() => null) : null,
      ]);
      this._connections = connections?.labConnections || [];
      // Pas nu zijn de keuzelijsten van het lab bekend: daarvóór kon niet blijken dat er maar
      // één afspraak of urgentie is. Zie _vulEnigeKeuzes.
      this._vulEnigeKeuzes();
      this._project = project?.project || project || null;
      this._borings = (borings?.borings || borings || []).filter(b => !b.deletedAt);
      this._mengmonsters = this._naarPaneel(this._order.analysisSamples || []);
      // Gerapporteerd: dan opent de pagina op de resultaten. Staat het bestand nog niet bij
      // ons, dan halen we het nu op; daarna komt het met de opdracht mee.
      this._uitslag = this._order.labResults || null;
      this._uitslagFout = '';
      this._verzondenTab = this._heeftUitslag ? 'resultaten' : 'opdracht';
      if (this._heeftUitslag && !this._uitslag) this._haalUitslag();
      // Teamleden voeden de projectleider-keuzelijst. Apart en zonder await-fout: kan het
      // niet, dan blijft het veld gewoon een vrij tekstveld.
      api.getTeamMembers()
        .then(data => { this._members = data?.members || []; })
        .catch(() => {});
      // Het beeldmerk van het bedrijf staat op het formulier. Apart en zonder await-fout:
      // lukt het niet, dan staat GeoVoice er.
      api.getCompanyProfile()
        .then(data => { this._bedrijfslogo = data?.logo || ''; })
        .catch(() => {});
      // Stil nalopen wat er nog ontbreekt: daar hangt de knop 'Verstuur labopdracht' van af.
      this._ververControle();
    } catch (e) {
      this._error = e.message || 'Labopdracht laden mislukt';
    } finally {
      this._loading = false;
    }
  }

  /**
   * Analysemonsters uit de backend → de vorm die het mengmonsterpaneel toont.
   *
   * De boringnaam staat niet bij een deelmonster; die zoeken we op bij de boringen die deze
   * pagina toch al heeft. Zonder naam zou een potje in het mandje 'onbekend' heten terwijl
   * het er in het profiel gewoon bij staat.
   */
  _naarPaneel(samples) {
    const naam = new Map(this._borings.map(b => [b.id, b.name]));
    return samples
      .filter(s => (s.sampleKind || 'mengmonster') === 'mengmonster')
      .map(s => ({
        // De vaste sleutel is het id: zo blijft het hetzelfde mengmonster als je het hernoemt.
        sleutel: s.id,
        code: s.name,
        id: s.id,
        notitie: s.reason || '',
        potjes: (s.parts || []).map(p => ({
          id: p.monsterId || p.id,
          naam: p.observations?.naam || p.barcode || '—',
          barcode: p.barcode || null,
          datum: p.observations?.datum || null,
          verpakking: p.observations?.verpakking || null,
          geroerd: p.observations?.geroerd,
          boringId: p.boringId || null,
          boringNaam: naam.get(p.boringId) || 'onbekend',
          van: p.upperDepth ?? 0,
          tot: p.lowerDepth ?? 0,
          // 'water' is de oude naam voor grondwater.
          matrix: s.matrix === 'water' ? 'grondwater' : (s.matrix || 'grond'),
          // Een fles van een watermonster: of hij gefiltreerd is, en bij welk watermonster.
          ...(p.observations?.gefiltreerd === undefined ? {} : { gefiltreerd: !!p.observations.gefiltreerd }),
          ...(p.observations?.watermonster ? { watermonster: p.observations.watermonster } : {}),
        })),
      }));
  }

  /**
   * Bewaren gebeurt vanzelf, kort nadat je stopt met klikken.
   *
   * Een knop 'opslaan' onder een scherm waarin je potjes aanklikt nodigt uit om werk kwijt te
   * raken: je klikt tien potjes aan, gaat naar het volgende tabblad en bent alles kwijt. De
   * korte vertraging voorkomt dat elke klik een verzoek is.
   */
  _mengmonstersGewijzigd(lijst) {
    this._mengmonsters = lijst;
    clearTimeout(this._bewaarTimer);
    this._bewaarstand = 'bezig';
    this._bewaarTimer = setTimeout(() => { this._bewaarTimer = null; this._bewaarMengmonsters(); }, 900);
  }

  /** Het backend-id van een mengmonster uit het mandje, of null als het nog niet bewaard is. */
  _idVan(m) {
    return m?.id || this._ids?.get(m?.sleutel) || (String(m?.sleutel || '').startsWith('nieuw-') ? null : m?.sleutel) || null;
  }

  /**
   * Bewaren, maar nooit twee keer tegelijk. Een tweede ronde terwijl de eerste nog loopt zou
   * een nieuw mengmonster twee keer aanmaken: geen van beide weet nog van het id van de ander.
   * Wat er intussen veranderde, gaat in de ronde direct daarna mee.
   */
  _bewaarMengmonsters() {
    if (this._bewaarLoopt) {
      this._bewaarOpnieuw = true;
      return this._bewaarLoopt;
    }
    this._bewaarLoopt = (async () => {
      do {
        this._bewaarOpnieuw = false;
        await this._bewaarRonde();
      } while (this._bewaarOpnieuw);
    })().finally(() => { this._bewaarLoopt = null; });
    return this._bewaarLoopt;
  }

  /**
   * De mengmonsters gelijktrekken met wat er in de backend staat, op hun vaste sleutel.
   *
   * Op naam ging dat mis zodra je kon hernoemen (dan leek een hernoemd mengmonster nieuw) of
   * een nummer opnieuw kon gebruiken (dan erfde een nieuw MM02 de pakketten van het weggegooide
   * MM02). Niet alles weggooien en opnieuw aanmaken: een analysemonster draagt de gekozen
   * pakketten. Dus: wat weg is verwijderen, wat nieuw is aanmaken, en van de rest alleen
   * bijwerken wat veranderd is.
   */
  async _bewaarRonde() {
    // Verstuurd: niets meer opslaan (de backend weigert het ook, met 409).
    if (!this._order || this._vergrendeld) return;
    if (!this._ids) this._ids = new Map();
    const orderId = this._order.id;
    const bestaand = new Map((this._order.analysisSamples || [])
      .filter(s => (s.sampleKind || 'mengmonster') === 'mengmonster')
      .map(s => [s.id, s]));
    const gewenst = this._mengmonsters.map(m => ({ m, id: this._idVan(m) }));
    const blijft = new Set(gewenst.map(g => g.id).filter(Boolean));

    const sleutel = (potjes) => potjes.map(p => `${p.id}:${p.van}-${p.tot}`).join('|');
    try {
      for (const [id] of bestaand) {
        if (!blijft.has(id)) await api.deleteLabAnalysisSample(id);
      }
      for (const { m, id } of gewenst) {
        const naam = m.code;
        const potjes = m.potjes || [];
        const parts = potjes.map(p => ({
          boringId: p.boringId || null,
          monsterId: p.id || null,
          // De barcode is wat het lab scant; de naam staat in observations en komt in het
          // aanleverbestand terug in <certificaat>.
          barcode: p.barcode || null,
          upperDepth: p.van ?? null,
          lowerDepth: p.tot ?? null,
          observations: {
            ...(p.verpakking ? { verpakking: p.verpakking } : {}),
            ...(p.naam ? { naam: p.naam } : {}),
            ...(p.datum ? { datum: p.datum } : {}),
            ...(p.boringNaam ? { boring: p.boringNaam } : {}),
            ...(p.geroerd === undefined ? {} : { geroerd: !!p.geroerd }),
            ...(p.gefiltreerd === undefined ? {} : { gefiltreerd: !!p.gefiltreerd }),
            ...(p.watermonster ? { watermonster: p.watermonster } : {}),
          },
        }));
        const velden = {
          name: naam,
          // Leeg en niet null: de backend slaat `null` over (?? undefined), dus een notitie
          // wissen zou dan niets doen.
          reason: m.notitie || '',
          sampleKind: 'mengmonster',
          // De monstersoort van de opdracht; een grondwaterpotje in een grondopdracht blijft grondwater
          // (het paneel waarschuwt daarvoor). 'grondwater' is de naam die de labkoppeling kent.
          matrix: ['asfalt', 'grondwater'].includes(this._order.sampleType) ? this._order.sampleType
            : potjes.some(p => p.matrix === 'water' || p.matrix === 'grondwater') ? 'grondwater' : 'grond',
          upperDepth: potjes.length ? Math.min(...potjes.map(p => p.van)) : null,
          lowerDepth: potjes.length ? Math.max(...potjes.map(p => p.tot)) : null,
        };
        const huidig = id ? bestaand.get(id) : null;
        if (!huidig) {
          const res = await api.createLabAnalysisSample(orderId, { ...velden, parts });
          // Het nieuwe id onthouden bij de tijdelijke sleutel: de volgende ronde werkt dit
          // mengmonster dan bij in plaats van het nog een keer aan te maken.
          if (res?.analysisSample?.id) this._ids.set(m.sleutel, res.analysisSample.id);
          continue;
        }
        const oud = (huidig.parts || []).map(p => ({ id: p.monsterId || p.id, van: p.upperDepth, tot: p.lowerDepth }));
        const anderePotjes = sleutel(oud) !== sleutel(potjes);
        const andereNotitie = (huidig.reason || '') !== (m.notitie || '');
        const andereNaam = huidig.name !== naam;
        // Een ander monstertype van de opdracht (asfalt of grond) verandert de matrix van elk
        // analysemonster, ook als er aan de potjes niets veranderde.
        const andereMatrix = (huidig.matrix || 'grond') !== velden.matrix;
        if (anderePotjes) await api.replaceLabAnalysisSampleParts(huidig.id, parts);
        if (anderePotjes || andereNotitie || andereNaam || andereMatrix) await api.updateLabAnalysisSample(huidig.id, velden);
      }
      // Opnieuw ophalen: de matrix in het tabblad Pakketten leest dezelfde analysemonsters.
      const res = await api.getLabOrder(orderId);
      this._order = res.labOrder;
      this._bewaarstand = 'klaar';
      this._ververControle();
    } catch (e) {
      this._error = e.message || 'Mengmonsters bewaren mislukt';
      this._bewaarstand = 'fout';
    }
  }

  /** Vanaf 'verstuurd' liggen de mengmonsters en de pakketten vast. */
  get _vergrendeld() {
    const o = this._order || {};
    return o.status === 'sent' || !!o.sentAt;
  }

  /**
   * Eerst controleren, dan pas het venster.
   *
   * Een bevestiging tonen die je daarna toch niet mag versturen is een omweg: dan lees je een
   * document door om vervolgens te horen dat er een barcode ontbreekt.
   */
  async _openVerstuur() {
    this._saving = true;
    this._verstuurfout = '';
    this._labfouten = [];
    try {
      const res = await api.validateLabOrder(this._order.id);
      if (res.labOrder) this._order = res.labOrder;
      this._ontbreekt = res.missing || [];
      this._verstuurOpen = true;
    } catch (e) {
      this._error = e.message || 'Controleren mislukt';
    } finally {
      this._saving = false;
    }
  }

  /**
   * De labopdracht weggooien.
   *
   * Alleen zolang hij hier staat: is hij verstuurd, dan ligt hij bij het lab en komt de uitslag
   * straks nog terug op dat ordernummer -- de backend weigert het dan ook. Met de mengmonsters
   * en potjes erbij, want die horen bij deze opdracht en nergens anders bij.
   */
  async _verwijder() {
    const nummer = this._order?.number || 'deze labopdracht';
    const mm = (this._order?.analysisSamples || []).length;
    const ok = await confirmDialog({
      title: `${nummer} weggooien?`,
      message: mm
        ? `De opdracht verdwijnt met ${mm} ${mm === 1 ? 'mengmonster' : 'mengmonsters'} en de gekozen pakketten. Dit is niet terug te draaien.`
        : 'De opdracht verdwijnt. Dit is niet terug te draaien.',
      confirmLabel: 'Ja, weggooien',
      danger: true,
    });
    if (!ok) return;
    this._saving = true;
    try {
      await api.deleteLabOrder(this._order.id);
      this._ga(this._terugAdres);
    } catch (e) {
      this._error = e.message || 'Labopdracht weggooien mislukt';
    } finally {
      this._saving = false;
    }
  }

  /** Het aanleverbestand als bestand, zonder iets te versturen. */
  async _downloadXml() {
    this._verstuurfout = '';
    try {
      const { blob, filename } = await api.downloadLabOrderXml(this._order.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      this._verstuurfout = e.message || 'Bestand ophalen mislukt';
    }
  }

  async _bevestigVerstuur() {
    this._saving = true;
    this._verstuurfout = '';
    this._labfouten = [];
    try {
      const res = await api.sendLabOrder(this._order.id);
      if (res.labOrder) this._order = res.labOrder;
      this._verstuurOpen = false;
    } catch (e) {
      this._verstuurfout = e.message || 'Versturen mislukt';
      // Het lab keurt af met een lijst; die hoort regel voor regel in beeld, want er staat
      // in welk veld of welk monster het misging.
      this._labfouten = e.errors || [];
      if (e.missing?.length) this._ontbreekt = e.missing;
    } finally {
      this._saving = false;
    }
  }

  /**
   * Het document dat je bevestigt.
   *
   * Dezelfde gegevens als wat er straks in de order-XML staat, in de volgorde waarin je ze
   * zou nalopen: eerst met wie je zaken doet, dan wat je stuurt. Locatie- en boorgegevens
   * staan er bewust niet in -- die blijven in GeoVoice en gaan niet mee naar het lab.
   */
  _renderVerstuur() {
    const conn = this._orderConnection;
    const klaar = !this._ontbreekt.length;

    return html`
      <div class="overlay" @click=${(e) => { if (e.target === e.currentTarget) this._verstuurOpen = false; }}>
        <div class="verstuurvenster">
          <header>
            <div>
              <strong>Labopdracht versturen naar ${conn?.displayName || 'het lab'}</strong>
              <span>Controleer deze gegevens. Na versturen kun je de mengmonsters en pakketten
                    niet meer wijzigen, en bevestig je de opdracht in @MIS -- pas daarna begint
                    het lab.</span>
            </div>
            <button class="sluit" @click=${() => { this._verstuurOpen = false; }}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>

          <div class="verstuurlijf">
            ${!klaar ? html`
              <div class="blokkade">
                <strong>${unsafeSVG(lucideIcon(TriangleAlert, 14))} Nog niet compleet</strong>
                <ul>${this._ontbreekt.map(m => html`<li>${m}</li>`)}</ul>
                <span>Vul dit aan en probeer het opnieuw; zo vindt het lab de fouten niet als eerste.</span>
              </div>` : ''}

            ${this._renderBlad()}
            ${this._verstuurfout ? html`
              <div class="blokkade kaal">
                <strong>${unsafeSVG(lucideIcon(TriangleAlert, 14))} Het lab heeft de opdracht niet aangenomen</strong>
                <p>${this._verstuurfout}</p>
                ${this._labfouten.length ? html`
                  <ul>${this._labfouten.map(f => html`<li>${f}</li>`)}</ul>` : ''}
                <span>De opdracht staat nog op concept; je kunt hem aanpassen en opnieuw versturen.</span>
              </div>` : ''}
          </div>

          <footer>
            <!-- Zonder te versturen: zo zie je wat er de deur uit gaat, en je kunt het bestand
                 door de Importmodule van Atmis halen om het formaat te controleren. -->
            <button class="btn" @click=${this._downloadPdf} ?disabled=${this._pdfBezig}>
              ${unsafeSVG(lucideIcon(FileDown, 14))} ${this._pdfBezig ? 'PDF maken…' : 'PDF'}
            </button>
            <button class="btn" @click=${this._downloadXml} ?disabled=${this._saving}>
              ${unsafeSVG(lucideIcon(FileDown, 14))} Bestand (SIKB)
            </button>
            <span class="rek"></span>
            <button class="btn" @click=${() => { this._verstuurOpen = false; }}>Terug</button>
            <button class="btn primary" ?disabled=${!klaar || this._saving} @click=${this._bevestigVerstuur}>
              ${unsafeSVG(lucideIcon(Send, 14))} Bevestigen en versturen
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  /**
   * De gegevens van het document, in de volgorde waarin ze op papier staan.
   *
   * Eén bron voor het blad op het scherm en de PDF: zie utils/labopdracht-document.js.
   */
  get _documentGegevens() {
    return buildLabopdrachtDocument({
      order: this._order || {},
      project: this._project,
      connection: this._orderConnection,
      borings: this._borings,
      companyName: auth.getActiveCompany()?.name || '',
      companyLogo: this._bedrijfslogo,
      verpakkingLabel: monsterVerpakkingLabel,
      // De code waaronder het lab rapporteert, uit het sjabloon van deze opdracht. Dezelfde
      // regel als in het aanleverbestand (@mv/models), zodat blad en bestand gelijk lopen.
      certificaatcode: ({ sampleName, meetpunten }) => certificateCode({
        format: this._order?.certificateFormat,
        projectCode: this._projectCode,
        measurementPoints: meetpunten,
        sampleName,
      }),
    });
  }

  /**
   * Het blad: de labopdracht zoals hij op papier staat.
   *
   * Op de pagina van een verstuurde opdracht én in de bevestiging vóór het versturen hetzelfde
   * stuk papier, en dezelfde indeling als de PDF: kerngegevens, de afspraken met het lab, een
   * matrix van welke analyse op welk analysemonster komt, en daaronder per analysemonster de
   * potten waaruit het lab het mengmonster samenstelt.
   *
   * Het derde blad van de PDF (de overdracht: aantallen tellen en aftekenen) staat hier niet:
   * dat is een papieren handeling, de koerier neemt geen scherm mee.
   */
  _renderBlad() {
    const d = this._documentGegevens;
    return html`
      <div class="blad">
        <header class="bladkop">
          <img class="bladlogo" src=${d.logo || logoUrl}
               alt=${d.logo ? (d.opdrachtgever || 'Opdrachtgever') : 'GeoVoice'}>
          <div class="bladtitel">
            <h3>${d.titel}</h3>
            <p>${[d.labNaam, d.labOrderId ? html`order-ID <span class="mono">${d.labOrderId}</span>` : '']
                 .filter(Boolean).map((x, i) => html`${i ? ' · ' : ''}${x}`)}</p>
            <span class="statuspil ${d.verzonden ? 'verzonden' : ''}">${d.statustekst}</span>
          </div>
        </header>
        <!-- De grondlagen van het beeldmerk als scheidingslijn. -->
        <div class="strata"><span></span><span></span><span></span></div>

        <div class="kerngegevens">
          ${d.kerngegevens.map(k => html`
            <div>
              <span class="label">${k.label}</span>
              <strong class=${k.mono ? 'mono' : ''}>${k.waarde || '—'}</strong>
            </div>`)}
        </div>

        <div class="gegevens">
          ${[d.gegevens.links, d.gegevens.rechts].map(kolom => html`
            <div class="kolom">
              ${kolom.map(r => html`
                <div class="regel">
                  <span>${r.k}</span>
                  <strong>${r.v || '—'}${r.extra ? html` <em class="mono">${r.extra}</em>` : ''}</strong>
                </div>`)}
            </div>`)}
        </div>

        ${d.opmerking ? html`
          <p class="opmerking"><span class="label">Opmerking</span>${d.opmerking}</p>` : ''}

        <h4 class="sectie">
          Analyses per analysemonster
          <em>${d.aantalMengmonsters} ${d.aantalMengmonsters === 1 ? 'analysemonster' : 'analysemonsters'} ·
              ${d.analysecodes.length} ${d.analysecodes.length === 1 ? 'analysecode' : 'analysecodes'}</em>
        </h4>
        ${this._renderMatrix(d)}

        <h4 class="sectie">
          Opbouw analysemonsters
          <em>${d.aantalPotten} ${d.aantalPotten === 1 ? 'pot' : 'potten'}</em>
        </h4>
        <p class="intro">
          Per analysemonster staan hieronder de deelmonsters (potten) waaruit het lab het
          mengmonster samenstelt, met barcode, meetpunt en traject. Het lab rapporteert per
          analysemonster op de certificaatcode.
        </p>
        ${d.mengmonsters.map(m => this._renderMengmonsterkaart(m))}
        ${d.mengmonsters.length ? '' : html`<p class="leegregel">Nog geen analysemonsters.</p>`}
      </div>
    `;
  }

  /**
   * De matrix: welke analyse komt op welk analysemonster.
   *
   * Zo zie je in één oogopslag of elk monster de analyse krijgt die je bedoelde -- een gat in
   * een kolom valt op, een ontbrekende regel in een opsomming niet. Welke kant hij op staat
   * bepaalt het document (zie labopdracht-document.js): de kortste van de twee wordt de
   * kolomas, anders loopt de tabel de bladzijde uit.
   */
  _renderMatrix(d) {
    const mx = d.matrix;
    if (!mx.rijen.length || !mx.kolommen.length) {
      return html`<p class="leegregel">Nog geen analyses gekozen.</p>`;
    }
    return html`
      <div class="matrixvak">
        <table class="matrix">
          <thead>
            <tr>
              <th>${mx.rijkop}</th>
              ${mx.vaste.map(v => html`<th class=${v === 'Potten' ? 'num' : ''}>${v}</th>`)}
              ${mx.kolommen.map(k => html`
                <th class="hc">
                  <span class="code">${k.titel}</span>
                  ${k.onder ? html`<span class="desc">${k.onder}</span>` : ''}
                </th>`)}
            </tr>
          </thead>
          <tbody>
            ${mx.rijen.map(r => html`
              <tr>
                <td class="naam">
                  ${r.titel}${r.onder ? html`<em class="rijonder">${r.onder}</em>` : ''}
                </td>
                ${r.vast.map((v, i) => html`<td class=${i === 1 ? 'num' : ''}>${v}</td>`)}
                ${r.vinkjes.map(v => html`<td class="c">${v ? html`<span class="vink"></span>` : ''}</td>`)}
              </tr>`)}
          </tbody>
          <tfoot>
            <tr>
              <td colspan=${1 + mx.vaste.length}>${mx.voettitel}</td>
              ${mx.totalen.map(t => html`<td class="c">${t}</td>`)}
            </tr>
          </tfoot>
        </table>
      </div>
    `;
  }

  /** Eén analysemonster: waar het uit bestaat en wat het lab ermee doet. */
  _renderMengmonsterkaart(m) {
    return html`
      <article class="kaart">
        <div class="kaartkop">
          <div class="kaartnaam"><small>Analysemonster</small>${m.naam}</div>
          <span class="tag">${m.matrix}</span>
        </div>
        <div class="kaartmeta">
          ${[['Certificaatcode', m.certificaatcode], ['Monsternamedatum', m.monsternamedatum]]
            .map(([k, v]) => html`
              <div><span class="label">${k}</span><strong>${v || '—'}</strong></div>`)}
        </div>
        <div class="kaartanalyses">
          <span class="label">Analyses</span>
          ${m.analyses.length ? m.analyses.map(a => html`
            <span class="chip"><em class="mono">${a.code}</em>${a.naam}</span>`)
            : html`<span class="chip leeg">geen analyses gekozen</span>`}
        </div>
        <table class="doctabel deel">
          <thead>
            <tr>
              <th>Deelmonster</th><th>Barcode</th><th>Meetpunt</th>
              <th class="num">Van (cm-mv)</th><th class="num">Tot (cm-mv)</th>
              <th>Datum</th><th>Verpakking</th><th class="c">Geroerd</th>
            </tr>
          </thead>
          <tbody>
            ${m.deelmonsters.length ? m.deelmonsters.map(x => html`
              <tr>
                <td>${x.naam || '—'}</td>
                <td class="mono">${x.barcode || html`<em class="mist">geen barcode</em>`}</td>
                <td>${x.meetpunt || '—'}</td>
                <td class="num">${x.van || '—'}</td>
                <td class="num">${x.tot || '—'}</td>
                <td>${x.datum || '—'}</td>
                <td>${x.verpakking || '—'}</td>
                <td class="c">${x.geroerd || '—'}</td>
              </tr>`) : html`
              <tr><td class="leegregel" colspan="8">Geen deelmonsters</td></tr>`}
          </tbody>
        </table>
        <!-- Eerst waar het monster uit bestaat, dan waarom juist deze potten bij elkaar gaan.
             Die toelichting kan een alinea zijn en krijgt daarom de volle breedte. -->
        ${m.notitie ? html`
          <div class="kaartopmerking">
            <span class="label">Opmerking</span>
            <p>${m.notitie}</p>
          </div>` : ''}
      </article>
    `;
  }

  /**
   * Een verstuurde labopdracht is geen formulier meer.
   *
   * De drie tabbladen zijn er om een opdracht samen te stellen. Ligt de opdracht eenmaal bij
   * het lab, dan wil je maar twee dingen: weten waar hij staat, en zien wat je verstuurd hebt
   * (of wat er terugkwam). Daarom bovenaan dezelfde vier stappen als in het overzicht, met
   * direct eronder wat er nu van jou verwacht wordt; daaronder de opdracht en de resultaten.
   */
  _renderVerzonden() {
    const o = this._order || {};
    const uitslag = this._heeftUitslag;
    const tabs = uitslag
      ? [['resultaten', 'Resultaten'], ['opdracht', 'Labopdracht']]
      : [['opdracht', 'Labopdracht'], ['resultaten', 'Resultaten']];
    const tab = uitslag ? this._verzondenTab : 'opdracht';
    return html`
      <section class="verzonden">
        <div class="standrij ${uitslag && heeftCertificaat(o.labKey) ? 'met-certificaat' : ''}">
          <div class="stand ${this._fase}">
            ${this._renderStappen()}
            ${this._renderNuAanJou()}
            <div class="standfeiten">
              ${this._feit(`Ordernummer bij ${this._labNaam}`, o.labOrderId)}
              ${this._feit('Batchnummer', o.labBatchId)}
              ${uitslag
                ? this._feit('Gerapporteerd', this._uitslag?.reportedOn ? formatDate(this._uitslag.reportedOn, { month: '2-digit' }) : 'Ja')
                : this._feit(`Status bij ${this._labNaam}`, labStatusPresentatie(o.labStatusName)?.label || 'Nog niet opgehaald')}
              ${uitslag ? '' : html`
                <div class="feit">
                  <span>Stand opgehaald</span>
                  <strong class="metknop">
                    ${this._verversBezig ? 'ophalen…' : o.lastStatusPollAt ? this._wanneer(o.lastStatusPollAt) : 'nog niet'}
                    <button class="tekstknop" @click=${this._verversStand}
                            ?disabled=${this._verversBezig || this._verversOpSlot}
                            title=${this._verversOpSlot ? 'Net opgehaald — over een paar tellen kun je het weer proberen.'
                                    : `Nu bij ${this._labNaam} kijken hoe het ervoor staat`}>ververs</button>
                  </strong>
                </div>`}
            </div>
            ${this._verstuurfout ? html`
              <div class="blokkade kaal">
                <strong>${unsafeSVG(lucideIcon(TriangleAlert, 14))} ${this._verstuurfout}</strong>
              </div>` : ''}
          </div>
          ${uitslag && o.labOrderId && heeftCertificaat(o.labKey) ? html`
            <lab-certificaat variant="kaart" .labOrderNumber=${o.labOrderId}
                             .batchId=${o.labBatchId || ''} .labName=${this._labNaam}></lab-certificaat>` : ''}
        </div>

        <nav class="tabs verzondentabs">
          ${tabs.map(([id, naam]) => {
            const dicht = id === 'resultaten' && !uitslag;
            const aantal = id === 'resultaten' && this._uitslag ? uitslagTabel(this._uitslag).aantal : 0;
            return html`
              <button class=${tab === id ? 'active' : ''} ?disabled=${dicht}
                      title=${dicht ? `De resultaten verschijnen hier zodra ${this._labNaam} heeft gerapporteerd` : ''}
                      @click=${() => { this._verzondenTab = id; }}>
                ${naam}${aantal ? html`<em class="telling">${aantal}</em>` : ''}
                ${dicht ? html`<small>· nog niet binnen</small>` : ''}
              </button>`;
          })}
        </nav>

        ${tab === 'resultaten' ? this._renderResultaten() : this._renderBladMetDownloads()}
      </section>
    `;
  }

  /**
   * Het verstuurde blad, met rechts ernaast de twee downloads van precies dit document: het
   * blad als PDF, en het aanleverbestand zoals het naar het lab ging. Het certificaat van het
   * lab hoort hier niet bij: dat is een ander document, met een eigen kaart bovenaan.
   */
  _renderBladMetDownloads() {
    const tegel = (titel, uitleg, klik, bezig = false) => html`
      <button class="downloadtegel" @click=${klik} ?disabled=${bezig}>
        <span class="icoon">${unsafeSVG(lucideIcon(FileDown, 17))}</span>
        <span><strong>${titel}</strong><small>${uitleg}</small></span>
      </button>`;
    return html`
      <div class="bladrij">
        ${this._renderBlad()}
        <aside class="bladdownloads">
          ${tegel(this._pdfBezig ? 'PDF maken…' : 'Labopdracht (PDF)', 'Dit blad, om te bewaren of mee te sturen',
            this._downloadPdf, this._pdfBezig)}
          ${tegel('Bestand (SIKB)', `Het aanleverbestand zoals het naar ${this._labNaam} ging`, this._downloadXml)}
        </aside>
      </div>`;
  }

  /** De fase van deze opdracht; zie utils/lab-fase.js. */
  get _fase() {
    return faseVan(this._order);
  }

  /**
   * Dezelfde vier stappen als in het overzicht, maar met datums: wanneer aangemaakt, wanneer
   * verstuurd, en wat er nu gebeurt. De streepjes tot en met de huidige stap kleuren in de
   * kleur van de fase; de huidige stap zelf ook in tekst.
   */
  _renderStappen() {
    const o = this._order || {};
    const fase = this._fase;
    const afgekeurd = isAfgekeurd(o);
    const lab = this._labNaam;
    const stand = labStatusPresentatie(o.labStatusName);
    const atmis = !!atmisOrderUrl(this._bevestigConnection);
    // De stappen hier zijn niet de fases van het overzicht: 'Verstuurd' is een eigen stap, en
    // bevestigen is dus de derde. Bij het lab staat die derde stap (Bevestigd) aan.
    const huidig = { concept: 1, bevestigen: 3, lab: 3, resultaten: 4 }[fase];
    const dag = (v) => formatDate(v, { month: '2-digit' });
    const stappen = [
      { label: 'Concept', datum: o.createdAt ? `Aangemaakt ${dag(o.createdAt)}` : '' },
      { label: `Verstuurd naar ${lab}`, datum: o.sentAt ? formatDateTime(o.sentAt) : '' },
      fase === 'bevestigen'
        ? { label: atmis ? 'Bevestigen in @MIS' : `Bevestigen bij ${lab}`, datum: 'Nu aan jou' }
        : afgekeurd
          ? { label: 'Afgekeurd', datum: `${stand?.label || 'Afgekeurd'} door ${lab}` }
          : { label: 'Bevestigd',
              datum: fase === 'lab'
                ? (stand && stand.label !== 'Bevestigd' ? `${stand.label} · ${lab} analyseert` : `${lab} analyseert`)
                : (o.labConfirmedAt ? formatDateTime(o.labConfirmedAt) : '') },
      { label: 'Resultaten',
        datum: fase === 'resultaten'
          ? (this._uitslag?.reportedOn ? dag(this._uitslag.reportedOn) : 'Binnen')
          : `Na analyse door ${lab}` },
    ];
    return html`
      <ol class="stappen ${afgekeurd ? 'afgekeurd' : ''}">
        ${stappen.map((s, i) => {
          const nr = i + 1;
          const soort = nr < huidig || fase === 'resultaten' ? 'klaar' : nr === huidig ? 'nu' : 'later';
          return html`
            <li class=${soort}>
              <i></i>
              <strong>${s.label}</strong>
              <span>${s.datum}</span>
            </li>`;
        })}
      </ol>`;
  }

  /**
   * Wat er nu van jou verwacht wordt, direct onder de stappen.
   *
   * Versturen zet de opdracht bij SGS klaar, maar het lab begint pas als iemand hem in @MIS
   * bevestigt -- tot die tijd staat hij daar als 'Concept'. Wie dat niet weet, denkt dat de
   * monsters in behandeling zijn terwijl er niets gebeurt, en dat merk je pas als de uitslag
   * uitblijft. Vandaar deze oproep, met de knop erbij naar de juiste omgeving: test en
   * productie hebben elk hun eigen adres.
   */
  _renderNuAanJou() {
    const o = this._order || {};
    const lab = this._labNaam;
    if (isAfgekeurd(o)) {
      return html`
        <div class="nuaanjou rood">
          ${unsafeSVG(lucideIcon(TriangleAlert, 18))}
          <div>
            <strong>${lab} heeft de opdracht niet in behandeling genomen</strong>
            <span>${labStatusPresentatie(o.labStatusName)?.uitleg || 'Neem contact op met het lab.'}</span>
          </div>
        </div>`;
    }
    if (this._fase !== 'bevestigen') return '';
    const conn = this._bevestigConnection;
    const url = atmisOrderUrl(conn);
    return html`
      <div class="nuaanjou">
        ${unsafeSVG(lucideIcon(TriangleAlert, 18))}
        <div>
          <strong>Nu aan jou: bevestig de opdracht ${url ? 'in @MIS' : `bij ${lab}`}</strong>
          <span>De opdracht staat bij ${lab} klaar als concept. Het lab begint pas met analyseren
                nadat je hem daar bevestigt${conn && conn.environment !== 'production' ? ' (testomgeving)' : ''}.</span>
        </div>
        ${url ? html`
          <a class="btn mis" href=${url} target="_blank" rel="noopener noreferrer">
            ${unsafeSVG(lucideIcon(ExternalLink, 14))} Openen in @MIS
          </a>` : ''}
      </div>`;
  }

  /**
   * De koppeling waarmee je bevestigt. De omgeving van de koppeling bepaalt het portaal: een
   * bedrijf met de testkoppeling (zoals lokaal en op staging) gaat naar atmis-test, productie
   * naar atmis zelf. Is de koppeling van de opdracht niet meer actief, dan volstaat een andere
   * van hetzelfde lab.
   */
  get _bevestigConnection() {
    const o = this._order || {};
    return this._orderConnection
      || (this._connections || []).find(c => c.labKey === (o.labKey || 'sgs'))
      || null;
  }

  /** 'vandaag 16:41' voor vandaag, anders datum en tijd. */
  _wanneer(waarde) {
    const d = parseTimestamp(waarde);
    if (!d) return '—';
    return d.toDateString() === new Date().toDateString()
      ? `vandaag ${formatTime(waarde)}`
      : formatDateTime(waarde);
  }

  /**
   * Nu bij het lab kijken hoe het ervoor staat, zonder eerst naar het overzicht te gaan.
   *
   * Het lab kent geen vraag per opdracht: we vragen de stand van alle opdrachten op (de
   * backend remt dat af, zie POLL_PAUZE_MS) en lezen daarna deze opdracht opnieuw. Het knopje
   * staat daarna tien tellen op slot, zodat je ziet dat je klik is aangekomen.
   */
  async _verversStand() {
    if (this._verversBezig || this._verversOpSlot) return;
    this._verversBezig = true;
    this._verversOpSlot = true;
    clearTimeout(this._slotTimer);
    this._slotTimer = setTimeout(() => { this._verversOpSlot = false; }, 10_000);
    this._verstuurfout = '';
    try {
      const res = await api.refreshLabOrderStatuses(365, true);
      if (res?.fouten?.length) this._verstuurfout = `Het lab gaf een melding terug: ${res.fouten.join(' · ')}`;
      const vers = await api.getLabOrder(this._order.id);
      const hadUitslag = this._heeftUitslag;
      this._order = vers.labOrder;
      this._uitslag = this._order.labResults || this._uitslag;
      // Net gerapporteerd: dan is dat wat je wilt zien.
      if (!hadUitslag && this._heeftUitslag) {
        this._verzondenTab = 'resultaten';
        if (!this._uitslag) this._haalUitslag();
      }
    } catch (e) {
      this._verstuurfout = `De stand bij het lab kon niet worden opgehaald. ${e.message || ''}`.trim();
    } finally {
      this._verversBezig = false;
    }
  }

  /** Het lab heeft gerapporteerd: er is een resultaatbestand en een analysecertificaat. */
  get _heeftUitslag() {
    return this._order?.resultsStatus === 'available' && !!this._order?.labOrderId;
  }

  /**
   * Het resultaatbestand bij het lab ophalen. De backend bewaart het daarna, dus dit gebeurt
   * één keer per opdracht; een gerapporteerde uitslag verandert niet meer.
   */
  async _haalUitslag() {
    this._uitslagBezig = true;
    this._uitslagFout = '';
    try {
      const res = await api.fetchLabOrderResults(this._order.labOrderId);
      this._uitslag = res?.labStatus?.results || null;
      if (!this._uitslag) this._uitslagFout = 'Het lab gaf geen resultaten terug voor deze opdracht.';
    } catch (e) {
      this._uitslagFout = e.message || 'Resultaten ophalen mislukt';
    } finally {
      this._uitslagBezig = false;
    }
  }

  /**
   * Waar een analysemonster uit bestaat, voor onder de kolomkop: de boringen en het
   * diepste bereik. Het lab kent alleen de naam; de samenstelling staat bij ons.
   */
  _herkomst(analysisSampleId, naam) {
    const mm = this._mengmonsters.find(m => m.id === analysisSampleId)
      || this._mengmonsters.find(m => m.code === naam);
    if (!mm?.potjes?.length) return '';
    const boringen = [...new Set(mm.potjes.map(p => p.boringNaam))];
    const van = Math.min(...mm.potjes.map(p => p.van));
    const tot = Math.max(...mm.potjes.map(p => p.tot));
    return `${boringen.join(', ')} · ${van}–${tot} cm`;
  }

  /**
   * De resultaten: bepalingen onder elkaar, analysemonsters naast elkaar, zoals op het
   * certificaat. Zonder toetsing: het resultaatbestand zegt niets over normen.
   */
  _renderResultaten() {
    const r = this._uitslag;
    const lab = this._labNaam;
    if (this._uitslagBezig || (!r && !this._uitslagFout)) {
      return html`
        <div class="uitslag leeg">
          ${unsafeSVG(lucideIcon(RefreshCw, 16))} Resultaten ophalen bij ${lab}…
        </div>`;
    }
    if (!r) {
      return html`
        <div class="uitslag leeg fout">
          <span>${unsafeSVG(lucideIcon(TriangleAlert, 15))} ${this._uitslagFout}</span>
          <button class="btn" @click=${this._haalUitslag}>Opnieuw proberen</button>
        </div>`;
    }
    const t = uitslagTabel(r);
    const breed = t.kolommen.length + 2;
    return html`
      <div class="uitslag">
        <div class="uitslagkop">
          <div>
            <h3>Analyseresultaten</h3>
            <span>${lab} · rapport ${r.certificateNumber || this._order.labBatchId || '—'}
              ${r.reportedOn ? html` · gerapporteerd ${formatDate(r.reportedOn)}` : ''}
              · ${t.kolommen.length} ${t.kolommen.length === 1 ? 'analysemonster' : 'analysemonsters'}
              · ${t.aantal} ${t.aantal === 1 ? 'bepaling' : 'bepalingen'}</span>
          </div>
        </div>
        <div class="tabelrol">
          <table class="meetwaarden">
            <thead>
              <tr>
                <th class="bepaling">Bepaling</th>
                <th class="eenheid">Eenheid</th>
                ${t.kolommen.map(k => html`
                  <th class="monster">
                    <strong>${k.name}</strong>
                    <span>${this._herkomst(k.analysisSampleId, k.name)}</span>
                  </th>`)}
              </tr>
            </thead>
            <tbody>
              ${t.groepen.map(g => html`
                <tr class="groep"><th colspan=${breed}>${g.naam}</th></tr>
                ${g.rijen.map(rij => html`
                  <tr>
                    <td class="bepaling">${rij.label}</td>
                    <td class="eenheid ${rij.eenheid ? '' : 'code'}"
                        title=${rij.eenheid ? '' : `Eenheidcode ${rij.eenheidCode} uit de codelijst van het lab`}>
                      ${rij.eenheid || rij.eenheidCode}
                    </td>
                    ${rij.cellen.map(w => html`
                      <td class="waarde ${w && onderGrens(w) ? 'grens' : ''}">${w ? meetwaardeTekst(w) : ''}</td>`)}
                  </tr>`)}`)}
            </tbody>
          </table>
        </div>
        <p class="uitslagvoet">
          Meetwaarden uit het resultaatbestand van ${lab}. Een waarde met &lt; ligt onder de
          rapportagegrens. De toetsing aan achtergrond- en interventiewaarden zit hier niet in.
          Methoden, accreditatie en ondertekening staan in het analysecertificaat.
        </p>
      </div>
    `;
  }

  /**
   * De labopdracht als PDF -- hetzelfde blad, maar dan om mee te sturen of af te drukken.
   *
   * jsPDF komt pas binnen als je erom vraagt: het is een zware bibliotheek en de meeste mensen
   * die deze pagina openen willen alleen even kijken.
   */
  async _downloadPdf() {
    this._pdfBezig = true;
    this._verstuurfout = '';
    try {
      const { labopdrachtPdf } = await import('../utils/labopdracht-pdf.js');
      const { blob, filename } = await labopdrachtPdf(this._documentGegevens);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      this._verstuurfout = e.message || 'PDF maken mislukt';
    } finally {
      this._pdfBezig = false;
    }
  }

  _feit(label, waarde) {
    return html`<div class="feit"><span>${label}</span><strong>${waarde || '—'}</strong></div>`;
  }

  _vulFormulier() {
    const o = this._order || {};
    this._form = {
      projectLeaderName: o.projectLeaderName || '',
      projectLeaderEmail: o.projectLeaderEmail || '',
      // Orderdatum: aanpasbaar, in tegenstelling tot createdAt. Oude opdrachten zonder
      // orderdatum vallen terug op hun aanmaakdatum, zodat het veld nooit leeg oogt.
      orderDate: o.orderDate || (o.createdAt || '').slice(0, 10),
      sampleType: o.sampleType || 'grond',
      reference: o.reference || '',
      certificateFormat: normalizeCertificateFormat(o.certificateFormat) || DEFAULT_CERTIFICATE_FORMAT,
      // Is er maar één keuze, dan is "Niet gekozen" geen keuze maar een extra klik. Bij
      // meerdere blijft het leeg: dan moet je zelf bepalen welke afspraak geldt.
      urgencyCode: o.urgencyCode || this._enigeKeuze('urgencies'),
      agreementCode: o.agreementCode || this._enigeKeuze('agreements'),
      sampleDeliveryDate: o.sampleDeliveryDate || '',
      deliveryDate: o.deliveryDate || '',
      remarks: o.remarks || '',
    };
  }

  get _projectCode() {
    const p = this._project || {};
    return p.project_code || p.projectCode || '';
  }

  get _projectNaam() {
    const p = this._project || {};
    return p.project_name || p.name || p.project_code || 'Project';
  }

  get _terugAdres() {
    if (this.terug === 'alle') return '/labopdrachten';
    const id = this._order?.projectId;
    return id ? `/projects/${id}/labopdrachten` : '/labopdrachten';
  }

  _ga(url, e) {
    e?.preventDefault();
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  render() {
    if (this._loading) return html`<div class="muted">Labopdracht laden...</div>`;
    if (!this._order) return html`<div class="error">${this._error || 'Labopdracht niet gevonden.'}</div>`;
    const order = this._order;
    const open = this._ontbreekt.length;
    const potjes = this._mengmonsters.reduce((n, m) => n + m.potjes.length, 0);
    const adminOpen = this._checklist.filter(c => !c.klaar && c.tab === 'admin').length;
    return html`
      <div class="detail">
        <!-- De kop van de opdracht: een witte band met daaronder de tabbladen, zoals de kop van
             een project. Zo lees je de opdracht als één ding met drie kanten, en niet als een
             titel boven drie losse pagina's. -->
        <header class="opdrachtkop">
          <div class="koprij">
            <a class="terug" href=${this._terugAdres} @click=${(e) => this._ga(this._terugAdres, e)}
               title=${this.terug === 'alle' ? 'Terug naar alle labopdrachten' : 'Terug naar de labopdrachten van dit project'}>
              ${unsafeSVG(lucideIcon(ChevronLeft, 16))}
            </a>
            <div class="koptitel">
              <div class="titelrij">
                <h2>${order.number || 'Nieuwe labopdracht'}</h2>
                ${this._vergrendeld
                  ? html`<span class="fasepil ${this._fase} ${isAfgekeurd(order) ? 'afgekeurd' : ''}">${isAfgekeurd(order) ? 'Afgekeurd' : FASES[this._fase].label}</span>`
                  : html`<span class="statuslabel concept"><span></span>CONCEPT</span>`}
              </div>
              <!-- Het project in de onderregel en aanklikbaar: hier hoort de opdracht bij, en
                   één klik brengt je naar het projectoverzicht. -->
              <!-- Lab en klantcode staan hier niet meer (ze staan bij Administratieve data): de
                   onderregel zegt wat de opdracht omvat. -->
              <p class="opdrachtregel">
                ${this._mengmonsters.length} ${this._mengmonsters.length === 1 ? 'analysemonster' : 'analysemonsters'}${this._vergrendeld
                  ? `, ${potjes} ${potjes === 1 ? 'pot' : 'potten'}`
                  : ` uit ${this._borings.length} ${this._borings.length === 1 ? 'boring' : 'boringen'}`}
              </p>
            </div>
            <!-- Het project als eigen tegel naast de titel: hier hoort de opdracht bij, en één klik
                 brengt je naar het projectoverzicht. -->
            ${order.projectId ? html`
              <a class="projecttegel" href=${`/projects/${order.projectId}`} @click=${(e) => this._ga(`/projects/${order.projectId}`, e)}
                 title=${`Naar het project ${this._projectNaam}`}>
                <span class="mv-caps">Project</span>
                <strong>${this._project?.project_code || this._projectNaam}</strong>
                ${this._project?.project_code && this._projectNaam !== this._project.project_code ? html`<small>${this._projectNaam}</small>` : ''}
              </a>` : ''}
            <span class="rek"></span>
            <div class="head-actions">
              <!-- Verstuurd: geen knoppen in de kop. De downloads gaan over het labopdrachtblad
                   en staan daarom naast dat blad (zie _renderBladMetDownloads); op het tabblad
                   Resultaten zouden ze over het verkeerde document lijken te gaan. -->
              ${this._vergrendeld ? '' : html`
                <button class="btn gevaar" @click=${this._verwijder} ?disabled=${this._saving}
                        title="Deze labopdracht weggooien">
                  ${unsafeSVG(lucideIcon(Trash2, 15))}
                </button>
                <!-- De controle loopt vanzelf mee (zie _ververControle). Deze knop laat zien wát
                     er nog ontbreekt; het getal erop zegt of dat de moeite van kijken waard is. -->
                <button class="btn controleer" @click=${this._toonControle} ?disabled=${this._controleBezig}
                        title="Nalopen wat het lab nog nodig heeft">
                  ${unsafeSVG(lucideIcon(CheckCircle2, 15))} Controleer
                  ${open ? html`<em class="telling open">${open}</em>` : ''}
                </button>
                <button class="btn primary" @click=${this._openVerstuur}
                        ?disabled=${this._saving || !!open}
                        title=${open
                          ? 'De labopdracht is nog niet compleet. Klik op Controleer om te zien wat er ontbreekt.'
                          : 'De opdracht ter bevestiging bekijken en versturen'}>
                  ${unsafeSVG(lucideIcon(Send, 14))} Verstuur labopdracht
                </button>`}
            </div>
          </div>

          ${this._vergrendeld ? '' : html`
            <nav class="tabs">
              ${[
                ['admin', 'Administratieve data', adminOpen ? `${adminOpen} open` : 'compleet', adminOpen ? 'open' : 'klaar'],
                ['composition', 'Samenstelling', `${potjes} ${potjes === 1 ? 'potje' : 'potjes'}`, ''],
                ['packages', 'Pakketten', String(this._mengmonsters.length), ''],
              ].map(([id, naam, badge, soort]) => html`
                <button class=${this._step === id ? 'active' : ''} @click=${() => this._kiesTab(id)}>
                  ${naam}<em class="telling ${soort}">${badge}</em>
                </button>`)}
            </nav>`}
        </header>

        ${this._error ? html`<div class="error">${this._error}</div>` : ''}

        <!-- Verstuurd: dan is er niets meer samen te stellen, en vervangt het document de
             drie tabbladen. Zie _renderVerzonden. -->
        ${this._vergrendeld ? this._renderVerzonden() : html`
          ${this._step === 'admin' ? this._renderAdminStep() : ''}
          <!-- Samenstelling blijft in de DOM, ook op de andere tabbladen: Flip werkt overal
               met zijn filter en mandje, en de kaart houdt zo zijn zoom. -->
          ${this._renderCompositionStep()}
          ${this._step === 'packages' ? this._renderPakketten() : ''}
          <lab-assistent
            .labOrderId=${order.id}
            .projectId=${order.projectId}
            .getPanel=${() => this._paneel}
            .mengmonsters=${this._mengmonsters}
            @toon-samenstelling=${() => this._kiesTab('composition')}
          ></lab-assistent>`}
      </div>
      ${this._verstuurOpen ? this._renderVerstuur() : ''}
      ${this._controleOpen ? this._renderControle() : ''}
    `;
  }

  /** Het onderdeel Samenstelling; draagt het mandje en het filter. */
  get _paneel() {
    return this.renderRoot?.querySelector('lab-mengmonsters') || null;
  }

  get _labNaam() {
    const o = this._order || {};
    return this._orderConnection?.displayName || (o.labKey ? labPresentation(o.labKey).name : 'Lab');
  }

  /** De knop Controleer: eerst bewaren wat nog openstaat, dan nalopen en de uitslag tonen. */
  async _toonControle() {
    if (this._adminTimer) { clearTimeout(this._adminTimer); this._adminTimer = null; await this._bewaarAdmin(); }
    if (this._bewaarTimer) { clearTimeout(this._bewaarTimer); this._bewaarTimer = null; await this._bewaarMengmonsters(); }
    this._ververControle(true);
  }

  /**
   * Een veld wijzigen bewaart het ook, kort nadat je stopt met typen.
   *
   * Er stond een knop 'Opslaan en naar mengmonsters' onder het formulier. Die werd vergeten: je
   * vulde de urgentie in, klikte naar Samenstelling en was hem kwijt. Nu bewaart het formulier
   * zichzelf, net als de mengmonsters, en zegt de checklist ernaast wat er nog mist.
   */
  _setForm(field, value) {
    this._form = { ...this._form, [field]: value };
    if (this._vergrendeld) return;
    clearTimeout(this._adminTimer);
    this._adminstand = 'bezig';
    this._adminTimer = setTimeout(() => { this._adminTimer = null; this._bewaarAdmin(); }, 800);
  }

  async _bewaarAdmin() {
    if (!this._order || this._vergrendeld) return;
    try {
      const anderType = (this._order.sampleType || 'grond') !== (this._form.sampleType || 'grond');
      const res = await api.updateLabOrderAdminData(this._order.id, this._form);
      if (res.labOrder) this._order = res.labOrder;
      // De analysemonsters dragen het monstertype als matrix; die gaan mee.
      if (anderType && this._mengmonsters.length) await this._bewaarMengmonsters();
      // Er kan intussen verder getypt zijn; dan staat er al een nieuwe bewaarronde klaar.
      if (!this._adminTimer) this._adminstand = 'klaar';
      this._ververControle();
    } catch (e) {
      this._adminstand = 'fout';
      this._error = e.message || 'Administratieve data opslaan mislukt';
    }
  }

  /**
   * Wat er nog moet gebeuren voordat de opdracht weg kan, in de volgorde waarin je het doet.
   *
   * De eerste vier staan op het tabblad Administratieve data en tellen voor het getal op dat
   * tabblad; de rest hoort bij Samenstelling en Pakketten. De laatste regel is de controle van
   * de backend: die kent ook barcodes en labcodes, en is waar 'Verstuur' op wacht.
   */
  get _checklist() {
    const f = this._form || {};
    const gevuld = (v) => !!String(v || '').trim();
    const mm = this._mengmonsters || [];
    const bewaard = new Map((this._order?.analysisSamples || []).map(s => [s.id, s]));
    return [
      { tekst: 'Projectleider en e-mailadres ingevuld', klaar: gevuld(f.projectLeaderName) && gevuld(f.projectLeaderEmail), tab: 'admin',
        veld: gevuld(f.projectLeaderName) ? 'projectLeaderEmail' : 'projectLeaderName' },
      { tekst: `Koppeling met ${this._labNaam} actief`, klaar: !!this._orderConnection, tab: 'admin' },
      { tekst: 'Urgentie en afspraak gekozen', klaar: gevuld(f.urgencyCode) && gevuld(f.agreementCode), tab: 'admin',
        veld: gevuld(f.urgencyCode) ? 'agreementCode' : 'urgencyCode' },
      // Eén datum voor de hele opdracht (het veld 'Datum monsters' hierboven), niet per monster.
      { tekst: 'Datum monsters (levering aan lab) ingevuld', klaar: gevuld(f.sampleDeliveryDate), tab: 'admin', veld: 'sampleDeliveryDate' },
      { tekst: 'Analysemonsters samengesteld', klaar: mm.length > 0 && mm.every(m => m.potjes.length), tab: 'composition' },
      { tekst: 'Elk analysemonster heeft een pakket', klaar: mm.length > 0 && mm.every(m => (bewaard.get(this._idVan(m))?.packages || []).length), tab: 'packages' },
      { tekst: this._ontbreekt.length
          ? `Controle: nog ${this._ontbreekt.length} ${this._ontbreekt.length === 1 ? 'punt' : 'punten'}`
          : 'Controle: compleet', klaar: !this._ontbreekt.length, controle: true },
    ];
  }

  /**
   * Is er maar één afspraak of urgentie, kies hem dan zelf.
   *
   * Bij de meeste bedrijven staat er precies één raamofferte tegenover het lab. Dan is "Niet
   * gekozen" geen keuze maar een extra klik, en erger: de opdracht blijft 'niet compleet' om
   * iets waar niets aan te kiezen valt.
   *
   * Het gaat meteen mee naar de backend. Anders staat de afspraak wel in beeld maar niet in de
   * opdracht, en telt de controle hem alsnog als ontbrekend -- precies de verwarring die dit
   * moet wegnemen. Alleen lege velden: wat de opdracht zelf draagt blijft staan.
   */
  async _vulEnigeKeuzes() {
    if (this._vergrendeld) return;
    const aanvulling = {};
    for (const [veld, lijst] of [['urgencyCode', 'urgencies'], ['agreementCode', 'agreements']]) {
      if ((this._form?.[veld] || '').trim() || (this._order?.[veld] || '')) continue;
      const code = this._enigeKeuze(lijst);
      if (code) aanvulling[veld] = code;
    }
    if (!Object.keys(aanvulling).length) return;

    this._form = { ...this._form, ...aanvulling };
    try {
      const res = await api.updateLabOrderAdminData(this._order.id, aanvulling);
      if (res.labOrder) this._order = res.labOrder;
      this._ververControle();
    } catch (_) {
      // Lukt het bewaren niet, dan staat de keuze er in elk geval; de volgende wijziging in het
      // formulier neemt hem mee.
    }
  }

  /**
   * De code van de enige beschikbare optie in een lablijst, of '' bij geen of meerdere.
   *
   * Bewust hier en niet in de render: daar een formulierwaarde zetten laat het scherm zichzelf
   * opnieuw tekenen, en dan zit je in een lus.
   */
  _enigeKeuze(listName) {
    const list = this._orderConnection?.[listName] || [];
    return list.length === 1 ? (list[0].code || '') : '';
  }

  /**
   * De opdracht nalopen: wat er nog ontbreekt voordat het lab hem aanneemt.
   *
   * Het draait ook vanzelf -- bij het openen en na elke wijziging -- want daar hangt de knop
   * 'Verstuur labopdracht' van af. Zonder die stille controle zou je pas bij het versturen
   * horen dat er een barcode mist, en dat is precies de omweg die we wilden vermijden.
   *
   * @param {boolean} toon  de uitslag in beeld brengen (de knop Controleer), of stil bijwerken
   */
  async _ververControle(toon = false) {
    if (!this._order || this._vergrendeld) return;
    this._controleBezig = true;
    if (toon) this._error = null;
    try {
      const res = await api.validateLabOrder(this._order.id);
      if (res.labOrder) this._order = res.labOrder;
      this._ontbreekt = res.missing || [];
      if (toon) this._controleOpen = true;
    } catch (e) {
      // Stil mislukken mag geen melding geven bij het openen van de pagina; dan staat er een
      // rode regel waar je niets om vroeg. Versturen controleert sowieso nog een keer.
      if (toon) this._error = e.message || 'Controle mislukt';
    } finally {
      this._controleBezig = false;
    }
  }

  /**
   * Wat er ontbreekt, gegroepeerd zoals je het naloopt.
   *
   * De backend geeft één platte lijst terug ('urgentie', 'MM01: barcode voor een potje op
   * 0-20 cm'). Achter elkaar gezet leest dat als een muur tekst; per mengmonster gebundeld
   * weet je meteen waar je moet zijn.
   */
  get _ontbreektGegroepeerd() {
    const groepen = new Map();
    for (const regel of this._ontbreekt) {
      const { kop, tekst } = this._groepVan(regel);
      if (!groepen.has(kop)) groepen.set(kop, []);
      groepen.get(kop).push(tekst.charAt(0).toUpperCase() + tekst.slice(1));
    }
    return [...groepen].map(([kop, items]) => ({ kop, items }));
  }

  /**
   * Waar een ontbrekend ding bij hoort.
   *
   * De backend geeft één platte lijst; 'MM01: barcode ...' hoort bij dat mengmonster, de rest
   * bij de administratieve gegevens. Eén plek waar dat bepaald wordt, want het venster en de
   * telling op het tabblad hoorden hetzelfde getal te laten zien en deden dat niet.
   */
  _groepVan(regel) {
    const m = /^([^:]+):\s*(.+)$/.exec(regel);
    if (m) return { kop: m[1], tekst: m[2] };
    if (/^mengmonsters$/i.test(regel)) {
      return { kop: 'Mengmonsters', tekst: 'er is nog geen mengmonster samengesteld' };
    }
    return { kop: 'Administratieve gegevens', tekst: regel };
  }

  /**
   * De uitslag van de controle.
   *
   * Als venster en niet als regel boven de pagina: die regel werd bij tien ontbrekende dingen
   * een lint van honderd tekens dat over de hele breedte liep en nergens naar verwees.
   */
  _renderControle() {
    const klaar = !this._ontbreekt.length;
    return html`
      <div class="overlay" @click=${(e) => { if (e.target === e.currentTarget) this._controleOpen = false; }}>
        <div class="controlevenster">
          <header>
            <div>
              <strong>${klaar ? html`${unsafeSVG(lucideIcon(CheckCircle2, 17))} De labopdracht is compleet`
                : html`${unsafeSVG(lucideIcon(TriangleAlert, 17))} Nog niet compleet`}</strong>
              <span>${klaar
                ? 'Alles wat het lab nodig heeft staat erin. Je kunt de opdracht versturen.'
                : `Vul dit aan en controleer opnieuw; zo vindt het lab de fouten niet als eerste.`}</span>
            </div>
            <button class="sluit" @click=${() => { this._controleOpen = false; }}>
              ${unsafeSVG(lucideIcon(X, 16))}
            </button>
          </header>

          ${klaar ? '' : html`
            <div class="controlelijf">
              ${this._ontbreektGegroepeerd.map(groep => html`
                <div class="groep">
                  <strong>${groep.kop}</strong>
                  <ul>${groep.items.map(i => html`<li>${i}</li>`)}</ul>
                </div>`)}
            </div>`}

          <footer>
            <span class="rek"></span>
            <button class="btn" @click=${() => { this._controleOpen = false; }}>Sluiten</button>
            ${klaar ? html`
              <button class="btn primary" @click=${() => { this._controleOpen = false; this._openVerstuur(); }}>
                ${unsafeSVG(lucideIcon(Send, 14))} Verstuur labopdracht
              </button>` : ''}
          </footer>
        </div>
      </div>
    `;
  }

  /**
   * De administratieve data in vier groepen.
   *
   * Eén lange lijst velden dwong je alles te lezen om te vinden wat je zocht. Deze indeling
   * volgt waar de gegevens vandaan komen, en dat is precies de volgorde waarin je ze invult:
   * wat wij weten (basisgegevens), wat het lab bepaalt (laboratorium), wat je voor deze
   * bestelling kiest (labopdracht bestelling), en tot slot wat je er los bij wilt schrijven.
   *
   * Monstertype en certificering staan samen in "bestelling", en dat is geen toeval: samen
   * bepalen ze welke pakketten het lab aanbiedt bij de samenstelling.
   */
  _renderAdminStep() {
    const f = this._form;
    const order = this._order || {};
    return html`
      <div class="adminvlak">
        <!-- Eén paneel met een donkergroene kop, net als Samenstelling en Pakketten, en daarin
             twee vlakken met een lichtgrijze kop: wat jij over de opdracht weet, en wat je met
             het lab afspreekt. Monstertype en certificering staan bij de opdracht: samen bepalen
             ze welke pakketten het lab straks aanbiedt. -->
        <div class="admpaneel">
          <header class="admhoofd">
            <span class="mv-caps">Administratieve data</span>
            <span class="spacer"></span>
            ${this._bewaarregel()}
          </header>
          <section class="admkaart">
            ${this._kaartkop(CalendarDays, 'Opdracht')}
            <div class="form-grid">
              ${this._readonlyField('Nummer', order.number, null, null, null, true)}
              ${this._field('Orderdatum', 'orderDate', f.orderDate, 'date')}
              ${this._field('Orderreferentie', 'reference', f.reference)}
              ${this._select('Monstertype', 'sampleType', f.sampleType, monstertypeKeuzes(f.sampleType))}
              ${this._projectLeaderField(f)}
              ${this._projectLeaderEmailField(f)}
              ${this._certificaatveld(f.certificateFormat)}
            </div>
          </section>

          <section class="admkaart">
            ${this._kaartkop(FlaskConical, 'Laboratorium & levering', html`<span class="kaartnoot">Lab bepaalt welke velden gevraagd worden</span>`)}
            ${this._labtegel()}
            <div class="form-grid">
              ${this._labListField('Urgentie', 'urgencyCode', f.urgencyCode, 'urgencies')}
              ${this._labListField('Afspraak', 'agreementCode', f.agreementCode, 'agreements')}
              ${this._field('Datum monsters', 'sampleDeliveryDate', f.sampleDeliveryDate, 'date', true)}
              ${this._field('Eerste levering', 'deliveryDate', f.deliveryDate, 'date')}
            </div>
            <label class="opmerkingveld">
              <span>Opmerking voor het lab</span>
              <textarea rows="2" placeholder="Bijvoorbeeld: een monster met olie-geur graag apart behandelen."
                        .value=${f.remarks || ''} @input=${e => this._setForm('remarks', e.target.value)}></textarea>
            </label>
          </section>
        </div>

        ${this._renderChecklist()}
      </div>
    `;
  }

  _kaartkop(icoon, titel, rechts = '') {
    return html`
      <div class="admkop">
        <span class="kaarticoon">${unsafeSVG(lucideIcon(icoon, 14))}</span>
        <h3>${titel}</h3>
        <span class="spacer"></span>
        ${rechts}
      </div>`;
  }

  /** 'Opgeslagen' naast de kop: zonder opslaanknop wil je wel weten dát het bewaard is. */
  _bewaarregel() {
    const tekst = { bezig: 'Opslaan…', klaar: 'Opgeslagen', fout: 'Opslaan mislukt' }[this._adminstand];
    return tekst ? html`<span class="bewaarregel ${this._adminstand}">${tekst}</span>` : '';
  }

  /**
   * Het lab als tegel: wie het is, met welke klantcode, of de koppeling werkt, en hoe oud de
   * lijsten met urgenties, afspraken en pakketten zijn -- met de knop om ze nu op te halen.
   */
  _labtegel() {
    const order = this._order || {};
    const conn = this._orderConnection;
    const wanneer = conn?.productSyncedAt;
    const regel = this._synct ? `Gegevens ophalen bij ${this._labNaam}…`
      : this._syncmelding ? this._syncmelding
      : wanneer ? `Urgenties, afspraken en pakketten opgehaald: ${formatDateTime(wanneer)}`
      : 'Urgenties, afspraken en pakketten nog niet opgehaald';
    return html`
      <div class="labtegel">
        <div class="labrij">
          ${this._labMark(order.labKey, 34)}
          <div class="labnaam">
            <strong>${this._labNaam}</strong>
            <span>Klantcode ${order.labCustomerId || '–'}${this._infoTip('Je interne klantcode bij het lab.\nDeze komt uit je labkoppeling en is automatisch aan jouw bedrijf gekoppeld.')}</span>
          </div>
          ${conn
            ? html`<span class="statuslabel actief"><span></span>ACTIEF</span>`
            : html`<span class="statuslabel inactief"><span></span>GEEN KOPPELING</span>`}
        </div>
        <div class="ophaalrij">
          <span class="ophaalregel ${this._syncmelding ? 'fout' : ''}">${regel}</span>
          <button class="ophaalknop ${this._synct ? 'bezig' : ''}" ?disabled=${this._synct || !conn}
                  title="Afspraken, urgenties en pakketten nu opnieuw ophalen bij het lab. Gebeurt normaal vanzelf, hooguit een paar keer per dag."
                  @click=${() => this._haalProductlijst()}>
            ${unsafeSVG(lucideIcon(RefreshCw, 14))} Opnieuw ophalen
          </button>
        </div>
      </div>`;
  }

  /**
   * Klaar om te versturen: wat er nog moet gebeuren, als lijst met vinkjes en een balk.
   *
   * Plakt naast het formulier, zodat je bij elk veld dat je invult ziet wat het oplevert. Een
   * regel van een ander tabblad brengt je erheen; de laatste regel opent de volledige controle.
   */
  /**
   * Een open punt uit de checklist dat op deze tab hoort: naar dat veld scrollen, de cursor
   * erin, en het veld even laten oplichten zodat je ziet waar je bent.
   */
  _naarVeld(veld) {
    const label = this.renderRoot?.querySelector(`[data-veld="${veld}"]`);
    if (!label) return;
    label.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const invoer = label.querySelector('input, select, textarea, searchable-select');
    invoer?.focus({ preventScroll: true });
    label.classList.remove('oplichten');
    void label.offsetWidth; // de animatie opnieuw laten beginnen bij een tweede klik
    label.classList.add('oplichten');
  }

  _renderChecklist() {
    const lijst = this._checklist;
    const klaar = lijst.filter(c => c.klaar).length;
    const pct = Math.round((klaar / lijst.length) * 100);
    return html`
      <!-- Dezelfde tegel als de mengmonsterkolom bij Samenstelling: donkergroene kop met een
           klein woord erboven en de telling rechts, de inhoud eronder. -->
      <aside class="checklist">
        <div class="checkkop">
          <div class="checktitel">
            <span class="checkeyebrow">Versturen</span>
            <h3>Klaar om te versturen</h3>
          </div>
          <span class="checktelling">${klaar} van ${lijst.length} klaar</span>
          <span class="balk" aria-hidden="true"><span style="width:${pct}%"></span></span>
        </div>
        <div class="checklijf">
        <ul>
          ${lijst.map(c => {
            const klik = c.controle ? () => this._toonControle()
              : c.veld && !c.klaar ? () => this._naarVeld(c.veld)
              : c.tab && c.tab !== 'admin' ? () => this._kiesTab(c.tab) : null;
            return html`
              <li class=${[c.klaar ? 'klaar' : '', klik ? 'klikbaar' : ''].join(' ')}
                  @click=${klik || (() => {})}
                  title=${klik ? (c.controle ? 'Bekijk de controle' : c.veld && !c.klaar ? 'Naar dit veld' : 'Naar dit tabblad') : ''}>
                <span class="vak">${c.klaar ? html`<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff"
                  stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"></path></svg>` : ''}</span>
                <span>${c.tekst}</span>
              </li>`;
          })}
        </ul>
        <button class="btn primary vol" @click=${this._openVerstuur}
                ?disabled=${this._saving || !!this._ontbreekt.length}>
          ${unsafeSVG(lucideIcon(Send, 14))} Verstuur labopdracht
        </button>
        </div>
      </aside>`;
  }

  /**
   * Een veld dat vastligt: nummer, datum, laboratorium, klantcode.
   *
   * Wel tonen en niet weglaten, want het hoort bij wat er straks naar het lab gaat. Als
   * invoerveld vermomd zou het uitnodigen tot typen, dus het staat er als tekst.
   */
  _readonlyField(label, value, mark = null, tip = null, actie = null, mono = false) {
    return html`
      <div class="readonly">
        <span>${label}${tip ? this._infoTip(tip) : ''}</span>
        <strong class=${mono ? 'mono' : ''}>${mark}${value || '—'}${actie || ''}</strong>
      </div>
    `;
  }

  /**
   * De productlijst nu ophalen bij het lab.
   *
   * De lijst verandert zelden en wordt daarom hooguit een paar keer per dag ververst. Laat je
   * bij het lab een nieuwe afspraak of een nieuw pakket aanmaken, dan wil je dat meteen zien
   * en niet over zes uur.
   */
  async _haalProductlijst() {
    const id = this._orderConnection?.id || this._order?.labConnectionId;
    if (!id || this._synct) return;
    this._synct = true;
    this._syncmelding = '';
    this.requestUpdate();
    try {
      await api.syncLabConnectionProducts(id);
      const res = await api.getActiveLabConnections();
      this._connections = res?.labConnections || [];
      this._syncmelding = '';   // de nieuwe tijd zegt het al
    } catch (e) {
      this._syncmelding = e.message || 'Ophalen mislukt';
    } finally {
      this._synct = false;
      this.requestUpdate();
    }
  }

  /**
   * Uitleg-icoontje bij een label. Gebruikt de gedeelde hover-tooltip en niet `title`: die
   * wacht ongeveer een seconde, en dan heb je je vraag al opgegeven.
   */
  _infoTip(text) {
    return html`<span data-tip=${text} @mousemove=${tipMove} @mouseleave=${tipHide}
      style="display:inline-flex; vertical-align:-2px; margin-left:4px; color:var(--mv-text-muted); cursor:help;"
      >${unsafeSVG(lucideIcon(Info, 13))}</span>`;
  }

  /**
   * Het logovierkantje van een lab, zoals in de bedrijvenlijst op /admin/companies. Dezelfde
   * vorm op beide plekken: een lab hoort er overal hetzelfde uit te zien, anders moet je twee
   * keer leren wat je ziet.
   *
   * Zonder logo een gekleurd vierkantje met de eerste twee letters, zodat een lab dat we nog
   * niet kennen toch herkenbaar blijft.
   */
  _labMark(labKey, size = 32) {
    const lab = labPresentation(labKey);
    const box = `width:${size}px; height:${size}px; border-radius:var(--mv-radius-xs); flex:none;`;
    if (!lab.logo) {
      return html`<span style="${box} background:${lab.accent}; color:#fff; font-size:${Math.round(size * 0.4)}px; font-weight:700; display:flex; align-items:center; justify-content:center;">${lab.name.slice(0, 2).toUpperCase()}</span>`;
    }
    return html`<span style="${box} background:#fff; border:1px solid var(--mv-border); display:flex; align-items:center; justify-content:center; overflow:hidden;">
        <img src=${lab.logo} alt="" style="max-width:82%; max-height:82%; object-fit:contain;">
      </span>`;
  }

  _field(label, key, value, type = 'text', verplicht = false) {
    return html`
      <label class=${verplicht ? this._leeg(value) : ''} data-veld=${key}>
        <span>${label}${verplicht ? this._verplicht() : ''}</span>
        <input type=${type} .value=${value || ''} @input=${e => this._setForm(key, e.target.value)}>
      </label>
    `;
  }

  /**
   * Projectleider-e-mail: kies een collega of typ zelf iets.
   *
   * Dezelfde opzet als de boormeester-keuzelijst bij een boring, om dezelfde reden: meestal
   * is het iemand uit het eigen team, maar niet altijd — een opdracht kan op naam van een
   * externe projectleider staan. Een gesloten lijst zou dat onmogelijk maken.
   *
   * Kies je een collega en staat de naam nog leeg, dan wordt die erbij gezet. Overschrijven
   * doen we niet: wie de naam zelf heeft aangepast, meende dat.
   */
  /** Naam van een teamlid, of leeg als hij alleen een e-mailadres heeft. */
  _memberName(m) {
    return [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  }

  /**
   * Eén veld voor de projectleider: kies een collega, of typ zelf een naam.
   *
   * Er stonden twee velden naast elkaar, naam en e-mail, en allebei moest je invullen terwijl
   * de een meestal uit de ander volgt. Nu kies je één keer. In de lijst herken je een collega
   * aan zijn naam; onderliggend draagt die keuze het e-mailadres, want dat is wat naar het lab
   * gaat. Het e-mailveld verschijnt eronder zodra er een projectleider staat.
   *
   * Voor een teamlid is de waarde dus het e-mailadres en toont het veld de naam. Typ je zelf
   * iets, dan is de waarde precies die tekst en behandelen we hem als naam.
   */
  _projectLeaderField(f) {
    const options = (this._members || [])
      .filter(m => m.email && this._memberName(m))
      .map(m => ({ value: m.email, label: this._memberName(m) }));
    // Een naam die niet (meer) in het team zit, blijft zichtbaar in plaats van stil te verdwijnen.
    const known = options.some(o => o.value === (f.projectLeaderEmail || '').trim());
    const value = known ? f.projectLeaderEmail.trim() : (f.projectLeaderName || '');
    if (value && !known && !options.some(o => o.value === value)) options.unshift(value);
    return html`
      <label class=${this._leeg(f.projectLeaderName || f.projectLeaderEmail)} data-veld="projectLeaderName">
        <span>Projectleider${this._verplicht()}</span>
        <searchable-select
          allowCustom
          placeholder="— kies of typ —"
          .options=${options}
          .value=${value}
          @change=${e => this._setProjectLeader(e.detail.value)}>
        </searchable-select>
      </label>`;
  }

  /**
   * Het e-mailadres van de projectleider, altijd in beeld: het staat in de checklist, en een
   * veld dat pas verschijnt als je iets anders invult, zoek je.
   *
   * Koos je een collega, dan staat het adres er al. Typte je zelf een naam, dan is het veld
   * leeg en vul je het hier aan; zonder adres kan het lab de opdracht niet terugkoppelen.
   */
  _projectLeaderEmailField(f) {
    return html`
      <label class=${this._leeg(f.projectLeaderEmail)} data-veld="projectLeaderEmail">
        <span>E-mail projectleider${this._verplicht()}</span>
        <input type="email" .value=${f.projectLeaderEmail || ''} placeholder="naam@bedrijf.nl"
          @input=${e => this._setForm('projectLeaderEmail', e.target.value)}>
      </label>`;
  }

  /**
   * Kies je een collega als projectleider, dan volgt de naam mee — ook als er al een naam
   * stond. Eerder vulde die alleen een leeg veld, en dat gaf de omgekeerde fout van wat je
   * verwacht: je wisselt van projectleider, het e-mailadres verspringt, maar de naam van je
   * voorganger blijft staan en gaat zo naar het lab.
   *
   * Alleen wanneer er GEEN naam te halen valt, blijft het veld met rust: bij een zelf getypt
   * adres dat bij niemand in het team hoort, of bij een lid dat alleen een e-mailadres heeft.
   * Leegmaken zou daar wissen wat je met de hand had ingevuld.
   */
  /**
   * Eén keuze verwerken: een collega uit de lijst, of een zelf getypte naam.
   *
   * De waarde van een teamlid IS zijn e-mailadres (zie _projectLeaderField), dus daarmee
   * vinden we het lid terug en vullen we de naam. Staat de waarde niet in de ledenlijst, dan
   * heb je zelf iets getypt: dat is een naam, en het e-mailadres gaat leeg. Dat is expres --
   * anders blijft het adres van je voorganger onder een nieuwe naam staan, en precies die
   * combinatie gaat verkeerd naar het lab.
   */
  _setProjectLeader(value) {
    const gekozen = (value || '').trim();
    if (!gekozen) {
      this._setForm('projectLeaderName', '');
      this._setForm('projectLeaderEmail', '');
      return;
    }
    const member = (this._members || []).find(m => m.email === gekozen);
    if (member) {
      this._setForm('projectLeaderName', this._memberName(member) || gekozen);
      this._setForm('projectLeaderEmail', gekozen);
      return;
    }
    this._setForm('projectLeaderName', gekozen);
    this._setForm('projectLeaderEmail', '');
  }

  /** De koppeling waar deze opdracht aan hangt; die draagt de lijsten van het lab. */
  get _orderConnection() {
    if (!this._order) return null;
    return this._connections.find(c => c.id === this._order.labConnectionId)
      || this._connections.find(c => c.labKey === this._order.labKey)
      || null;
  }

  /**
   * Urgentie en afspraak zijn geen vrije tekst maar codes die het lab per klant vaststelt.
   * Ze komen uit de productlijst die bij de koppeling is opgehaald.
   *
   * Twee dingen bewust zo. Er staat altijd de leesbare omschrijving met de labcode erachter,
   * want "Asbest 5 wkd" zegt iets en "1" niets. En is de lijst er nog niet, dan blijft het
   * veld een tekstveld in plaats van een lege keuzelijst: zo kun je doorwerken en zie je wat
   * eraan schort in plaats van een dropdown die niet opengaat.
   */
  /**
   * Sterretje achter het label van een verplicht veld.
   *
   * `display:inline` staat er expres: de regel `label span { display:block }` hierboven is een
   * afstammingsselector en raakt dus ook dit spannetje, waardoor het sterretje op een eigen
   * regel belandde. En gedempt in plaats van rood: dit is een aanduiding vooraf, geen
   * foutmelding -- rood bewaren we voor wat er daadwerkelijk misgaat.
   */
  /**
   * Een verplicht veld dat nog leeg is krijgt een rode rand: zo zie je meteen wat er nog moet
   * voordat de opdracht weg kan (dezelfde velden als in 'Klaar om te versturen').
   */
  _leeg(value) {
    return String(value ?? '').trim() ? '' : 'leeg';
  }

  _verplicht() {
    return html`<span title="Verplicht" style="display:inline; margin:0 0 0 2px; color:var(--mv-text-muted);">*</span>`;
  }

  _labListField(label, key, value, listName) {
    const list = this._orderConnection?.[listName] || [];
    if (!list.length) {
      // Geen verwijzing naar beheerpagina's: die ziet een projectgebruiker niet eens, en
      // "vraag het een beheerder" is geen antwoord op de vraag wat hij nu moet doen. De lijst
      // komt van het lab, dus leeg betekent: er is niets met dit lab afgesproken. Klopt dat
      // volgens de gebruiker niet, dan is support de juiste weg.
      return html`
        <label data-veld=${key}>
          <span>${label}</span>
          <input type="text" .value=${value || ''} @input=${e => this._setForm(key, e.target.value)}>
          <small class="field-hint">
            Niets beschikbaar bij dit lab. Zijn er wel afspraken gemaakt?
            <a href="mailto:support@ecocharting.com?subject=Labopdracht: ${label.toLowerCase()} ontbreekt">Neem contact op met support</a>.
          </small>
        </label>`;
    }
    return html`
      <label class=${this._leeg(value)} data-veld=${key}>
        <span>${label}${this._verplicht()}</span>
        <select .value=${value || ''} @change=${e => this._setForm(key, e.target.value)}>
          <option value="">Niet gekozen</option>
          ${list.map(item => html`
            <option value=${item.code} ?selected=${String(item.code) === String(value)}>
              ${item.label} (${item.code})
            </option>`)}
        </select>
      </label>`;
  }

  _select(label, key, value, options) {
    return html`
      <label>
        <span>${label}</span>
        <select .value=${value || ''} @change=${e => this._setForm(key, e.target.value)}>
          ${options.map(([v, l]) => html`<option value=${v}>${l}</option>`)}
        </select>
      </label>
    `;
  }

  /**
   * Onder welke code het lab dit analysemonster rapporteert.
   *
   * Een keuzelijst en geen vrij tekstveld: de sjabloonwoorden moeten precies kloppen, en een
   * typefout merk je pas op het certificaat. `{meetpunt}` staat erin maar is niet de
   * aanbevolen vorm -- een mengmonster komt uit potten van meerdere boringen, en dan is er
   * niet één meetpunt; die namen worden dan samengevoegd.
   */
  _certificaatveld(waarde) {
    const huidig = normalizeCertificateFormat(waarde);
    const keuzes = CERTIFICATE_FORMATS.some(k => k.format === huidig)
      ? CERTIFICATE_FORMATS
      : [...CERTIFICATE_FORMATS, { format: huidig, label: huidig }];

    // Een voorbeeld met wat er nu in de opdracht staat: '{projectcode}/{analysemonster}' zegt
    // minder dan 'NEWNEN/MM01', en zo zie je meteen of het format past bij wat je verwacht.
    const eerste = this._mengmonsters.find(m => m.potjes.length) || this._mengmonsters[0];
    const voorbeeld = certificateCode({
      format: huidig,
      projectCode: this._projectCode || 'PROJECT',
      measurementPoints: eerste ? [...new Set(eerste.potjes.map(p => p.boringNaam))] : ['B01'],
      sampleName: eerste?.code || 'MM01',
    });

    return html`
      <label class="breed">
        <span>Certificaatcodering</span>
        <div class="certrij">
          <select .value=${huidig} @change=${e => this._setForm('certificateFormat', e.target.value)}>
            ${keuzes.map(k => html`
              <option value=${k.format} ?selected=${k.format === huidig}>${k.label}</option>`)}
          </select>
          <span class="voorbeeld mono" title="Zo heet het analysemonster straks op het certificaat">${voorbeeld}</span>
        </div>
      </label>
    `;
  }

  /**
   * Alleen het samenstellen. De tegels 'Te maken', 'Analysemonsters' en 'Pakketten' stonden
   * hieronder; die eerste twee zegt het paneel zelf al, en de pakketten krijgen een eigen
   * pagina. De boringen komen van deze pagina zelf, niet van een bovenliggend scherm.
   */
  /**
   * Van tabblad wisselen bewaart eerst wat er nog openstaat.
   *
   * De mengmonsters worden met een korte vertraging bewaard; klik je binnen die tijd door naar
   * Pakketten, dan zou je daar mengmonsters missen die je net hebt gemaakt.
   */
  _kiesTab(stap) {
    if (this._bewaarTimer) {
      clearTimeout(this._bewaarTimer);
      this._bewaarTimer = null;
      this._bewaarMengmonsters();
    }
    if (this._adminTimer) {
      clearTimeout(this._adminTimer);
      this._adminTimer = null;
      this._bewaarAdmin();
    }
    this._step = stap;
  }

  _renderCompositionStep() {
    const zichtbaar = this._step === 'composition';
    return html`
      ${zichtbaar && this._bewaarstand ? html`
        <p class="bewaarstand ${this._bewaarstand}">${{
          bezig: 'Mengmonsters bewaren…',
          klaar: 'Mengmonsters bewaard.',
          fout: 'Mengmonsters bewaren mislukt.',
        }[this._bewaarstand]}</p>` : ''}
      <lab-mengmonsters
        ?hidden=${!zichtbaar}
        .zichtbaar=${zichtbaar}
        .projectId=${this._order?.projectId || null}
        .borings=${this._borings}
        .bestaande=${this._mengmonsters}
        ?vergrendeld=${this._vergrendeld}
        .monstertype=${this._form?.sampleType || this._order?.sampleType || 'grond'}
        @mengmonsters-changed=${(e) => this._mengmonstersGewijzigd(e.detail.mengmonsters)}
        @naar-pakketten=${() => this._kiesTab('packages')}
      ></lab-mengmonsters>
    `;
  }

  /**
   * De matrix van analysemonsters en pakketten. De rijen komen uit het mandje; wat je daar aan
   * de mengmonsters verandert, gaat via Samenstelling, zodat er één plek is die ze beheert.
   */
  _renderPakketten() {
    const paneel = () => this._paneel;
    return html`
      <lab-pakketten
        .order=${this._order}
        .mengmonsters=${this._mengmonsters.map(m => ({ ...m, id: this._idVan(m) }))}
        .waarschuwingen=${(code) => paneel()?.waarschuwingen(code) || []}
        .hernoem=${(code, naam) => {
          const p = paneel();
          return p ? p.hernoemMengmonster(code, naam) : 'De samenstelling is nog niet geladen; probeer het zo nog eens.';
        }}
        ?vergrendeld=${this._vergrendeld}
        @order-changed=${(e) => { this._order = e.detail.labOrder; this._ververControle(); }}
        @mm-nieuw=${() => paneel()?.nieuwMengmonster()}
        @mm-verwijder=${(e) => paneel()?.verwijderMengmonster(e.detail.code)}
        @mm-notitie=${(e) => paneel()?.zetNotitie(e.detail.code, e.detail.tekst)}
      ></lab-pakketten>
    `;
  }


  static styles = css`${unsafeCSS(base)}
    :host { display:block; }
    @keyframes draai { to { transform:rotate(360deg); } }
    .mono { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; }

    /* ── De kop van de opdracht ─────────────────────────────────────────── */
    /* Kop zonder kaart, met tabs zoals Instellingen › Rapportage. */
    .opdrachtkop { margin:0 0 18px; padding:4px 0 0; }
    .koprij { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
    /* Terug als witte tegel, de titel ernaast, en het project als lichte tegel daarnaast. */
    .terug { width:44px; height:44px; flex:none; display:flex; align-items:center; justify-content:center;
             border:1px solid var(--mv-border-soft); border-radius:12px; background:var(--mv-surface);
             box-shadow:0 1px 3px rgba(16,28,18,.06); color:var(--mv-text); text-decoration:none; }
    .terug:hover { border-color:var(--mv-border); box-shadow:0 2px 6px rgba(16,28,18,.1); }
    .projecttegel { display:flex; flex-direction:column; gap:2px; padding:9px 18px; border-radius:12px; margin-left:12px;
                    background:color-mix(in srgb, var(--mv-surface) 70%, transparent); border:1px solid var(--mv-border-soft);
                    text-decoration:none; color:var(--mv-text); min-width:0; max-width:320px; }
    .projecttegel:hover { border-color:var(--mv-border); background:var(--mv-surface); }
    .projecttegel .mv-caps { font-size:.66rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:var(--mv-text-muted); }
    .projecttegel strong { font-size:1rem; font-weight:800; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .projecttegel small { font-size:.72rem; color:var(--mv-text-sub); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .koptitel { min-width:0; }
    .titelrij { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .titelrij h2 { margin:0; font-size:1.5rem; font-weight:900; letter-spacing:-0.03em; color:var(--mv-text); }
    .opdrachtregel { margin:4px 0 0; font-size:0.9rem; color:var(--mv-text-sub); }
    .opdrachtregel a { color:var(--mv-text); font-weight:700; text-decoration:none; }
    .opdrachtregel a:hover { color:var(--mv-primary); }
    .statuslabel { display:inline-flex; align-items:center; gap:6px; padding:4px 9px; border-radius:999px;
                 font-size:0.68rem; font-weight:900; letter-spacing:0.04em; white-space:nowrap; }
    .statuslabel > span { width:6px; height:6px; border-radius:3px; background:currentColor; }
    .statuslabel.concept { background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); }
    .statuslabel.verstuurd, .statuslabel.actief { background:var(--mv-primary-tint); color:var(--mv-primary-hover); }
    .statuslabel.inactief { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .btn.controleer svg { color:var(--mv-primary); }
    .btn.controleer .telling { margin-left:2px; }
    .opdrachtkop .tabs { margin:16px 0 0; border-bottom:1px solid var(--mv-border); gap:4px; }
    .opdrachtkop .tabs button { display:flex; align-items:center; gap:8px; padding:10px 14px; margin-bottom:-1px; font-family:inherit;
                                font-size:.875rem; font-weight:600; color:var(--mv-text-sub); border-bottom:2px solid transparent; }
    .opdrachtkop .tabs button.active { color:var(--mv-primary); font-weight:700; border-bottom-color:var(--mv-primary); }
    .opdrachtkop .tabs button.active .telling:not(.open) { background:var(--mv-primary-tint); color:var(--mv-primary-hover); }

    .telling { display:inline-flex; align-items:center; padding:1px 7px; border-radius:999px;
               background:var(--mv-surface-alt); color:var(--mv-text-sub); font-size:0.68rem; font-weight:800; font-style:normal; }
    .telling.open { background:var(--mv-warning-panel-bg, #fff6e5); color:var(--mv-warning-text, #8c6512); }

    /* ── Administratieve data ───────────────────────────────────────────── */
    /* Links de kaarten, rechts de checklist die meeplakt. align-items:start is wat sticky laat
       werken: een uitgerekte kolom heeft niets om binnen te plakken. */
    .adminvlak { display:grid; grid-template-columns:minmax(0,1fr) minmax(260px,340px); gap:18px; align-items:start;
                 padding-bottom:84px; }
    /* Zelfde opbouw als Samenstelling: een donkergroene kop over het paneel, lichtgrijze koppen
       per vlak over de hele breedte. */
    .admpaneel { border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface); min-width:0; overflow:hidden; }
    .admhoofd { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:14px 20px 12px; background:#22371f; }
    .admhoofd .mv-caps { font-size:.66rem; font-weight:900; letter-spacing:.08em; text-transform:uppercase; color:#a9c69f; }
    .admhoofd .bewaarregel, .admhoofd .bewaarregel.klaar { color:#cfe0c9; }
    .admhoofd .bewaarregel.fout { color:#ffb4b4; }
    .admhoofd .spacer, .admkop .spacer { flex:1; }
    .admkaart { padding:0 20px 22px; min-width:0; }
    .admkaart + .admkaart { border-top:1px solid var(--mv-border-soft); }
    .admkop { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin:0 -20px 18px; padding:9px 20px; min-height:22px;
              background:color-mix(in srgb, var(--mv-text) 5%, var(--mv-surface)); border-bottom:1px solid var(--mv-border-soft); }
    .kaarticoon { display:flex; color:var(--mv-text-sub); }
    .admkop h3 { margin:0; font-size:.66rem; font-weight:900; letter-spacing:.08em; text-transform:uppercase; color:var(--mv-text-sub); }
    .kaartnoot { font-size:0.74rem; font-weight:700; color:var(--mv-text-muted); }
    .bewaarregel { font-size:0.74rem; font-weight:700; color:var(--mv-text-muted); }
    .bewaarregel.klaar { color:var(--mv-primary); }
    .bewaarregel.fout { color:var(--mv-danger); }
    .admkaart .form-grid { grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:18px; }
    .admkaart label.breed { grid-column:1 / -1; }
    .certrij { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .certrij select { flex:1 1 240px; width:auto; }
    .voorbeeld { flex:0 1 auto; min-width:0; margin:0; padding:8px 12px; border-radius:8px; background:var(--mv-surface-alt);
                 border:1px solid var(--mv-border-soft); font-size:0.78rem; font-weight:700; color:var(--mv-text);
                 overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .readonly strong.mono { font-size:var(--mv-text-md); }
    .readonly strong { min-height:36px; padding:0 12px; border:1.5px solid var(--mv-border-soft); border-radius:var(--mv-radius-sm);
                       background:var(--mv-surface-alt); box-sizing:border-box; }
    .opmerkingveld { display:block; margin-top:18px; }
    .opmerkingveld textarea { min-height:64px; }

    .labtegel { margin-bottom:18px; padding:12px 14px; border:1.5px solid var(--mv-border); border-radius:10px;
                background:var(--mv-surface-alt); }
    .labrij { display:flex; align-items:center; gap:12px; }
    .labnaam { flex:1 1 auto; min-width:0; }
    .labnaam strong { display:block; font-size:var(--mv-text-md); font-weight:800; color:var(--mv-text); }
    .labnaam span { display:block; font-size:0.78rem; color:var(--mv-text-sub); }
    .ophaalrij { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:11px; padding-top:11px;
                 border-top:1px solid var(--mv-border); }
    .ophaalregel { flex:1 1 auto; min-width:0; font-size:0.72rem; line-height:1.45; color:var(--mv-text-sub); }
    .ophaalregel.fout { color:var(--mv-danger); }
    .ophaalknop { display:flex; align-items:center; gap:7px; flex:0 0 auto; padding:6px 11px; border-radius:8px;
                  border:1.5px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
                  font:inherit; font-size:0.78rem; font-weight:800; cursor:pointer; white-space:nowrap; }
    .ophaalknop svg { color:var(--mv-primary); }
    .ophaalknop:hover:not(:disabled) { border-color:var(--mv-primary); }
    .ophaalknop:disabled { opacity:.6; cursor:default; }
    .ophaalknop.bezig svg { animation:draai 0.9s linear infinite; }

    .checklist { position:sticky; top:12px; border:1px solid var(--mv-border); border-radius:12px; overflow:hidden;
                 background:var(--mv-surface); box-shadow:0 2px 10px rgba(16,28,18,.05); }
    .checkkop { display:flex; align-items:flex-start; gap:10px; flex-wrap:wrap; padding:14px 16px 13px; background:#22371f; }
    .checktitel { min-width:0; }
    .checkeyebrow { display:block; font-size:.62rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:#a9c69f; }
    .checkkop h3 { margin:2px 0 0; font-size:.95rem; font-weight:800; color:#fff; letter-spacing:0; text-transform:none; }
    .checktelling { margin-left:auto; text-align:right; font-size:.68rem; font-weight:700; color:#cfe0c9; white-space:nowrap; }
    .checkkop .balk { flex-basis:100%; display:block; height:5px; margin-top:10px; border-radius:3px; background:rgba(255,255,255,.16); overflow:hidden; }
    .checkkop .balk span { display:block; height:100%; border-radius:3px; background:#7cc261; transition:width .25s ease; }
    .checklijf { padding:14px 16px 16px; }
    /* Het veld waar je vanuit de checklist naartoe springt, licht even op. */
    label.oplichten { animation:oplichten 1.6s ease-out; border-radius:8px; }
    @keyframes oplichten { 0%, 35% { box-shadow:0 0 0 4px var(--mv-primary-tint); background:var(--mv-primary-tint); }
                           100% { box-shadow:0 0 0 4px transparent; background:transparent; } }
    .checklist ul { list-style:none; margin:0; padding:0; display:grid; gap:9px; }
    .checklist li { display:flex; align-items:flex-start; gap:9px; font-size:0.84rem; line-height:1.4; color:var(--mv-text-sub); }
    .checklist li.klaar { color:var(--mv-text); font-weight:600; }
    .checklist li.klikbaar { cursor:pointer; }
    .checklist li.klikbaar:hover > span:last-child { color:var(--mv-primary); text-decoration:underline; }
    .checklist .vak { width:17px; height:17px; flex:none; margin-top:1px; border-radius:50%; box-sizing:border-box;
                      border:1.5px solid var(--mv-border); background:var(--mv-surface);
                      display:flex; align-items:center; justify-content:center; }
    .checklist li.klaar .vak { background:var(--mv-primary); border-color:var(--mv-primary); }
    .btn.vol { width:100%; margin-top:16px; }

    .bewaarstand { margin:0 0 10px; font-size:0.78rem; color:var(--mv-text-muted); }
    .bewaarstand.klaar { color:var(--mv-primary); }
    .bewaarstand.fout { color:var(--mv-danger); }
    .bewaarstand.slot { font-weight:700; color:var(--mv-text-sub); }
    .vergrendeld { display:inline-flex; align-items:center; gap:6px; padding:6px 11px; border-radius:8px;
                   background:var(--mv-surface-alt); color:var(--mv-text-sub); font-size:0.78rem; font-weight:700; }

    /* ── Bevestiging vóór het versturen ─────────────────────────────────── */
    .overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex; align-items:center;
               justify-content:center; padding:4vh 2vw; z-index:1200; }
    .verstuurvenster { width:min(900px,100%); max-height:92vh; display:flex; flex-direction:column;
                       overflow:hidden; background:var(--mv-surface); border-radius:12px;
                       box-shadow:0 24px 60px rgba(0,0,0,.3); }
    .verstuurvenster header { display:flex; align-items:flex-start; gap:10px; padding:16px 18px 12px; }
    .verstuurvenster header strong { display:block; font-size:1.02rem; }
    .verstuurvenster header span { display:block; margin-top:4px; font-size:0.78rem; color:var(--mv-text-sub); line-height:1.45; }
    .verstuurvenster .sluit { margin-left:auto; border:none; background:none; color:var(--mv-text-sub); cursor:pointer; display:flex; }
    /* Het blad ligt op een grijze ondergrond, net als op de pagina: zo lees je het als een
       document en niet als de inhoud van een venster. */
    .verstuurlijf { flex:1; overflow:auto; padding:14px 18px 18px; background:var(--mv-surface-alt); }
    .verstuurlijf .blad { box-shadow:none; padding:24px 26px 22px; }
    .verstuurvenster footer { display:flex; align-items:center; gap:10px; padding:14px 18px 16px; }
    .rek { flex:1; }

    /* De uitslag van de controle: een venster, geen regel. Bij tien ontbrekende dingen werd
       die regel een lint over de hele breedte dat nergens naar verwees. */
    .controlevenster { width:min(520px,100%); max-height:88vh; display:flex; flex-direction:column;
                       overflow:hidden; background:var(--mv-surface); border-radius:12px;
                       box-shadow:0 24px 60px rgba(0,0,0,.3); }
    .controlevenster header { display:flex; align-items:flex-start; gap:10px; padding:16px 18px 12px; }
    .controlevenster header strong { display:flex; align-items:center; gap:8px; font-size:1rem; }
    .controlevenster header span { display:block; margin-top:5px; font-size:0.78rem;
                                   color:var(--mv-text-sub); line-height:1.45; }
    .controlevenster .sluit { margin-left:auto; border:none; background:none; color:var(--mv-text-sub);
                              cursor:pointer; display:flex; }
    .controlelijf { flex:1; overflow:auto; padding:0 18px; }
    .controlevenster footer { display:flex; align-items:center; gap:10px; padding:14px 18px 16px; }
    .groep { padding:10px 12px; margin-bottom:8px; border-radius:10px; background:var(--mv-danger-bg);
             color:var(--mv-danger); }
    .groep strong { font-size:0.72rem; font-weight:800; letter-spacing:.04em; text-transform:uppercase; }
    .groep ul { margin:5px 0 0; padding-left:18px; line-height:1.55; font-size:0.82rem; }

    /* Naast de verstuurknop: hoeveel er nog ontbreekt, en één klik naar wat precies. */
    .onaf { display:flex; align-items:center; gap:6px; padding:6px 11px; border-radius:8px;
            border:1px solid var(--mv-danger-border, rgba(185,28,28,.25)); background:var(--mv-danger-bg);
            color:var(--mv-danger); font-size:0.78rem; font-weight:700; font-family:inherit;
            cursor:pointer; }
    .onaf:hover { border-color:var(--mv-danger); }
    /* Weggooien staat naast versturen maar hoort er niet als gelijke bij: alleen het icoon,
       en pas rood zodra je hem aanwijst. */
    .btn.gevaar { padding-left:0.6rem; padding-right:0.6rem; color:var(--mv-text-sub); }
    .btn.gevaar:hover:not(:disabled) { border-color:var(--mv-danger); color:var(--mv-danger);
                                       background:var(--mv-danger-bg); }
    .compleet { display:flex; align-items:center; gap:6px; padding:6px 11px; border-radius:8px;
                background:var(--mv-primary-tint, rgba(75,157,53,.12));
                color:var(--mv-success-text, #166534); font-size:0.78rem; font-weight:700; }

    .feit span { display:block; font-size:0.68rem; font-weight:800; letter-spacing:.04em;
                 text-transform:uppercase; color:var(--mv-text-muted); }
    .feit strong { display:block; margin-top:2px; font-size:0.86rem; color:var(--mv-text); }

    .blokkade { margin-bottom:14px; padding:10px 12px; border-radius:10px; background:var(--mv-danger-bg);
                color:var(--mv-danger); font-size:0.78rem; }
    .blokkade strong { display:flex; align-items:center; gap:6px; font-size:0.82rem; }
    .blokkade ul { margin:6px 0; padding-left:18px; line-height:1.5; }
    .blokkade span { display:block; opacity:.85; }
    .blokkade.kaal { margin:12px 0 0; }
    .blokkade p { margin:6px 0; }

    /* ── De verzonden opdracht ──────────────────────────────────────────────
       Twee delen: een strook met de stand van zaken (dat verandert nog), en het blad zelf
       (dat ligt vast). Het blad heeft de breedte van een A4 zodat het scherm en de PDF
       hetzelfde document tonen. */
    /* De strook met de stand van zaken en het blad staan in één kolom van dezelfde breedte.
       Een balk over de volle pagina boven een blad van 900px leest als twee losse dingen. */
    .verzonden { display:flex; flex-direction:column; align-items:center; gap:16px; }
    .verzonden > .standrij, .verzonden > .verzondentabs { box-sizing:border-box; width:100%; }
    /* Met resultaten staat het certificaat ernaast als eigen kaart: een ander document, met
       een eigen wachttijd. */
    .standrij { display:grid; grid-template-columns:minmax(0,1fr); gap:16px; }
    .standrij.met-certificaat { grid-template-columns:minmax(0,1.5fr) minmax(280px,1fr); }
    .stand { box-sizing:border-box; display:flex; flex-direction:column; gap:18px; padding:20px 22px;
             border:1px solid var(--mv-border); border-radius:14px; background:var(--mv-surface); }

    /* De vier stappen: een streep per stap, de naam en een datum of uitleg eronder. Tot en met
       de huidige stap in de kleur van de fase (--fase-* in tokens.css). */
    .stappen { list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:6px; }
    .stappen li { display:flex; flex-direction:column; gap:3px; padding-right:12px; min-width:0; }
    .stappen i { display:block; height:5px; margin-bottom:6px; border-radius:3px; background:var(--mv-border); }
    .stappen strong { font-size:0.84rem; font-weight:600; color:var(--mv-text); }
    .stappen span { font-size:0.76rem; color:var(--mv-text-muted); }
    .stappen li.later strong { color:var(--mv-text-muted); }
    .stand.bevestigen .stappen li:not(.later) i { background:var(--fase-bevestigen); }
    .stand.bevestigen .stappen li.nu strong { color:var(--fase-bevestigen-tekst); }
    .stand.lab .stappen li:not(.later) i { background:var(--fase-lab); }
    .stand.lab .stappen li.nu strong { color:var(--fase-lab-tekst); }
    .stand.resultaten .stappen li i { background:var(--fase-resultaten); }
    .stappen.afgekeurd li:not(.later) i { background:var(--mv-danger); }
    .stappen.afgekeurd li.nu strong { color:var(--mv-danger); }

    /* Zandkleurig en niet rood: er is niets misgegaan, er moet alleen nog iets gebeuren. */
    .nuaanjou { display:flex; align-items:center; gap:16px; padding:14px 16px; border-radius:10px;
                border:1px solid var(--mv-warning-panel-border); background:var(--mv-warning-panel-bg);
                color:var(--mv-warning-text); }
    .nuaanjou > svg { flex:none; }
    .nuaanjou > div { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
    .nuaanjou strong { font-size:0.9rem; }
    .nuaanjou span { font-size:0.82rem; line-height:1.45; }
    .nuaanjou .btn.mis { flex:none; background:var(--mv-warning-text); border-color:var(--mv-warning-text);
                         color:var(--mv-surface); text-decoration:none; }
    .nuaanjou.rood { border-color:var(--mv-danger); background:var(--mv-danger-bg); color:var(--mv-danger); }

    .fasepil { padding:3px 9px; border-radius:999px; font-size:0.7rem; font-weight:700; letter-spacing:0.07em;
               text-transform:uppercase; background:var(--fase-concept-tint); color:var(--fase-concept-tekst); }
    .fasepil.bevestigen { background:var(--fase-bevestigen-tint); color:var(--fase-bevestigen-tekst); }
    .fasepil.lab { background:var(--fase-lab-tint); color:var(--fase-lab-tekst); }
    .fasepil.resultaten { background:var(--fase-resultaten-tint); color:var(--fase-resultaten-tekst); }
    .fasepil.afgekeurd { background:var(--mv-danger-bg); color:var(--mv-danger); }

    /* Vier feiten op een rij: het zijn korte waarden, en als vakken met eigen achtergrond
       zouden ze zwaarder wegen dan het document eronder. */
    .standfeiten { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr));
                   gap:8px 20px; padding-top:16px; border-top:1px solid var(--mv-border-soft, var(--mv-border)); }
    .feit strong.metknop { display:flex; align-items:center; gap:8px; }
    .tekstknop { border:none; background:none; padding:0; font:inherit; font-size:0.8rem; font-weight:600;
                 color:var(--mv-primary); cursor:pointer; }
    .tekstknop:disabled { color:var(--mv-text-muted); cursor:default; }

    /* Het blad op zijn A4-breedte in het midden, de downloads in een smalle kolom ernaast. */
    .bladrij { box-sizing:border-box; width:100%; display:grid; grid-template-columns:minmax(0,900px) 230px;
               gap:20px; justify-content:center; align-items:start; }
    .bladrij > .blad { align-self:start; }
    .bladdownloads { position:sticky; top:16px; display:flex; flex-direction:column; gap:10px; }
    .downloadtegel { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; text-align:left;
                     border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface);
                     font-family:inherit; color:var(--mv-text); cursor:pointer; }
    .downloadtegel:hover:not(:disabled) { border-color:var(--mv-primary); }
    .downloadtegel:disabled { opacity:.6; cursor:default; }
    .downloadtegel .icoon { width:32px; height:32px; flex:none; display:flex; align-items:center; justify-content:center;
                            border-radius:8px; background:var(--mv-primary-light); color:var(--mv-primary-hover); }
    .downloadtegel strong { display:block; font-size:0.86rem; font-weight:600; }
    .downloadtegel small { display:block; margin-top:2px; font-size:0.76rem; line-height:1.4; color:var(--mv-text-sub); }
    @media (max-width: 1250px) {
      .bladrij { grid-template-columns:minmax(0,900px); }
      .bladdownloads { position:static; flex-direction:row; flex-wrap:wrap; order:-1; }
      .downloadtegel { flex:1; min-width:220px; }
    }

    .verzondentabs { display:flex; gap:4px; border-bottom:1px solid var(--mv-border); margin:0; }
    .verzondentabs button { display:flex; align-items:center; gap:8px; padding:10px 14px; margin-bottom:-1px;
                            border:none; border-bottom:2px solid transparent; background:none; font-family:inherit;
                            font-size:0.9rem; font-weight:500; color:var(--mv-text-sub); cursor:pointer; }
    .verzondentabs button.active { color:var(--mv-primary-hover); font-weight:600; border-bottom-color:var(--mv-primary); }
    .verzondentabs button.active .telling { background:var(--mv-primary-tint); color:var(--mv-primary-hover); }
    .verzondentabs button:disabled { color:var(--mv-text-muted); cursor:default; }
    .verzondentabs small { font-size:0.76rem; font-weight:500; }

    @media (max-width: 900px) {
      .standrij.met-certificaat { grid-template-columns:minmax(0,1fr); }
      .stappen { grid-template-columns:repeat(2,minmax(0,1fr)); row-gap:12px; }
      .nuaanjou { flex-wrap:wrap; }
    }

    /* ── De resultaten ─────────────────────────────────────────────────────
       Zelfde breedte als de strook erboven. Meer analysemonsters dan er passen: de tabel
       schuift binnen de kaart, met de bepaling vast aan de linkerkant. */
    .uitslag { box-sizing:border-box; width:100%; border:1px solid var(--mv-border);
               border-radius:12px; background:var(--mv-surface); overflow:hidden; }
    .uitslag.leeg { display:flex; align-items:center; gap:10px; padding:18px 16px;
                    font-size:0.84rem; color:var(--mv-text-sub); }
    .uitslag.leeg.fout { justify-content:space-between; color:var(--mv-danger); }
    .uitslag.leeg.fout span { display:flex; align-items:center; gap:7px; }
    .uitslagkop { display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding:14px 16px;
                  border-bottom:1px solid var(--mv-border); }
    .uitslagkop h3 { margin:0; font-size:0.98rem; color:var(--mv-text); }
    .uitslagkop span { font-size:0.76rem; color:var(--mv-text-sub); }
    .tabelrol { overflow-x:auto; }
    .meetwaarden { width:100%; border-collapse:collapse; font-size:0.8rem; }
    .meetwaarden th, .meetwaarden td { padding:5px 12px; text-align:left; white-space:nowrap; }
    .meetwaarden thead th { position:sticky; top:0; background:var(--mv-surface-alt);
                            border-bottom:1px solid var(--mv-border); font-size:0.7rem; font-weight:800;
                            letter-spacing:.04em; text-transform:uppercase; color:var(--mv-text-muted);
                            vertical-align:bottom; }
    .meetwaarden thead th.monster { text-align:right; text-transform:none; letter-spacing:0; }
    .meetwaarden thead th.monster strong { display:block; font-size:0.84rem; color:var(--mv-text); }
    .meetwaarden thead th.monster span { display:block; font-weight:600; color:var(--mv-text-sub); }
    .meetwaarden .bepaling { position:sticky; left:0; background:var(--mv-surface); width:100%; }
    .meetwaarden thead .bepaling { background:var(--mv-surface-alt); }
    .meetwaarden tr.groep th { padding:12px 12px 4px; font-size:0.7rem; font-weight:800;
                               letter-spacing:.04em; text-transform:uppercase; color:var(--mv-primary); }
    .meetwaarden tbody tr:not(.groep) td { border-bottom:1px solid var(--mv-border-soft, var(--mv-border)); }
    .meetwaarden tbody tr:not(.groep):hover td { background:var(--mv-surface-alt); }
    .meetwaarden .eenheid { color:var(--mv-text-sub); }
    .meetwaarden .eenheid.code { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; color:var(--mv-text-muted); }
    .meetwaarden .waarde { text-align:right; font-variant-numeric:tabular-nums; font-weight:700; }
    /* Onder de rapportagegrens: wel gemeten, niets gevonden. Zachter, zodat het oog naar de
       echte vondsten gaat. */
    .meetwaarden .waarde.grens { font-weight:500; color:var(--mv-text-muted); }
    .uitslagvoet { margin:0; padding:10px 16px 14px; font-size:0.74rem; line-height:1.5;
                   color:var(--mv-text-muted); border-top:1px solid var(--mv-border); }

    /* ── Het blad ─────────────────────────────────────────────────────────
       Een A4 op het scherm, met dezelfde indeling als de PDF: kerngegevens, de afspraken met
       het lab, de analysematrix, en per analysemonster een kaart met de potten. De kleuren
       komen uit de huisstijl (het beeldmerk is groen, zand, aarde) en niet uit de tokens van
       de app: dit is een document en hoort er in elk thema hetzelfde uit te zien. */
    .blad { --bl-groen:#378136; --bl-groen-donker:#24592a; --bl-groen-zacht:#eaf3e7;
            --bl-groen-lijn:#cfe3ca; --bl-zand:#a8743f; --bl-zand-zacht:#f7f0e6;
            --bl-aarde:#523a22; --bl-inkt:#1e2a1f; --bl-gedempt:#6b7469; --bl-lijn:#e3e8e0;
            --bl-rij:#f8faf7;
            align-self:center; box-sizing:border-box; width:100%; max-width:900px;
            padding:30px 34px 26px; border:1px solid var(--mv-border); border-radius:12px;
            background:#fff; color:var(--bl-inkt); font-size:0.82rem; line-height:1.45;
            box-shadow:0 10px 30px rgba(15,23,42,.06); }
    .blad .mono { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; }
    .blad .label { display:block; font-size:0.62rem; font-weight:600; letter-spacing:.07em;
                   text-transform:uppercase; color:var(--bl-gedempt); }

    .bladkop { display:flex; align-items:center; justify-content:space-between; gap:20px; }
    /* Een vast vak voor het beeldmerk: het ene bedrijf heeft een liggend woordmerk, het
       andere een vierkant logo. Passend schalen houdt de kop in beide gevallen even hoog. */
    .bladlogo { height:66px; width:auto; max-width:230px; object-fit:contain;
                object-position:left center; }
    .bladtitel { text-align:right; }
    .bladtitel h3 { margin:0; font-size:1.55rem; font-weight:700; color:var(--bl-groen-donker);
                    line-height:1.05; }
    .bladtitel p { margin:3px 0 0; font-size:0.74rem; color:var(--bl-gedempt); }
    .statuspil { display:inline-block; margin-top:5px; padding:2px 9px; border-radius:999px;
                 font-size:0.67rem; font-weight:600; background:var(--bl-zand-zacht);
                 color:var(--bl-aarde); border:1px solid #e6d6bf; }
    .statuspil.verzonden { background:var(--bl-groen-zacht); color:var(--bl-groen-donker);
                           border-color:var(--bl-groen-lijn); }
    .strata { display:flex; height:5px; border-radius:3px; overflow:hidden; margin:12px 0 18px; }
    .strata span:nth-child(1) { flex:6; background:var(--bl-groen); }
    .strata span:nth-child(2) { flex:2; background:var(--bl-zand); }
    .strata span:nth-child(3) { flex:1; background:var(--bl-aarde); }

    .kerngegevens { display:grid; grid-template-columns:2.3fr .9fr .9fr .9fr; margin-bottom:16px;
                    border-top:1px solid var(--bl-lijn); border-bottom:1px solid var(--bl-lijn); }
    .kerngegevens > div { padding:8px 12px 8px 0; }
    .kerngegevens > div + div { padding-left:12px; border-left:1px solid var(--bl-lijn); }
    .kerngegevens strong { display:block; margin-top:2px; font-size:0.9rem; font-weight:600;
                           line-height:1.3; overflow-wrap:anywhere; }

    .gegevens { display:grid; grid-template-columns:1fr 1fr; column-gap:28px; }
    .gegevens .regel { display:grid; grid-template-columns:40% 60%; gap:8px; padding:3px 0;
                       border-bottom:1px solid var(--bl-lijn); }
    .gegevens .regel span { color:var(--bl-gedempt); }
    .gegevens .regel strong { font-weight:500; overflow-wrap:anywhere; }
    .gegevens .regel em { font-style:normal; color:var(--bl-gedempt); }

    .opmerking { margin:10px 0 0; padding:5px 11px; border-left:3px solid var(--bl-zand);
                 background:var(--bl-zand-zacht); border-radius:0 6px 6px 0; font-size:0.8rem; }
    .opmerking .label { display:inline-block; margin-right:10px; }

    .sectie { display:flex; align-items:baseline; gap:9px; margin:20px 0 9px; font-size:1rem;
              font-weight:600; }
    .sectie::before { content:""; width:4px; height:12px; border-radius:2px;
                      background:var(--bl-groen); align-self:center; }
    .sectie em { margin-left:auto; font-style:normal; font-size:0.75rem; font-weight:500;
                 color:var(--bl-gedempt); }
    .blad .intro { margin:-2px 0 12px; max-width:62ch; color:var(--bl-gedempt); font-size:0.78rem; }
    .blad .leegregel { color:var(--bl-gedempt); font-style:italic; }

    /* ── Tabellen op het blad ───────────────────────────────────────────── */
    /* Schuiven kan altijd: bij veel analyses én veel monsters past het hoe dan ook niet. */
    .matrixvak { border:1px solid var(--bl-lijn); border-radius:6px; overflow-x:auto; }
    .blad table { width:100%; border-collapse:collapse; font-size:0.78rem; }
    .blad th { padding:6px 9px; text-align:left; font-size:0.63rem; font-weight:600;
               letter-spacing:.05em; text-transform:uppercase; color:var(--bl-gedempt);
               border-bottom:1.5px solid var(--bl-groen-lijn); white-space:nowrap; }
    .blad td { padding:5px 9px; border-bottom:1px solid var(--bl-lijn); vertical-align:top; }
    .blad tbody tr:last-child td { border-bottom:none; }
    .blad td.num, .blad th.num { text-align:right; font-variant-numeric:tabular-nums; }
    .blad td.c, .blad th.c { text-align:center; }
    .blad .mist { font-style:normal; color:var(--mv-danger); }

    .matrix tbody tr:nth-child(even) td { background:var(--bl-rij); }
    .matrix td.naam { font-weight:600; white-space:nowrap; }
    .matrix td.naam .rijonder { display:block; margin-top:1px; font-style:normal; font-weight:400;
                                font-size:0.72rem; color:var(--bl-gedempt); white-space:normal;
                                max-width:34ch; }
    .matrix th.hc { text-align:center; vertical-align:bottom; text-transform:none;
                    letter-spacing:0; border-left:1px solid var(--bl-lijn); line-height:1.25;
                    white-space:normal; min-width:62px; }
    .matrix th.hc .code { display:block; font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
                          font-size:0.68rem; color:var(--bl-groen-donker); }
    .matrix th.hc .desc { display:block; font-weight:400; font-size:0.64rem; }
    .matrix td.c { border-left:1px solid var(--bl-lijn); }
    .matrix tfoot td { border-top:1.5px solid var(--bl-groen-lijn); border-bottom:none;
                       background:var(--bl-rij); color:var(--bl-gedempt); font-size:0.7rem; }
    .matrix tfoot td.c { border-left:1px solid var(--bl-lijn); }
    /* Het vinkje: een groene schijf met een wit haakje, net als in de PDF. */
    .vink { display:inline-block; width:16px; height:16px; border-radius:50%;
            background:var(--bl-groen); position:relative; vertical-align:middle; }
    .vink::after { content:""; position:absolute; left:5.6px; top:3.2px; width:3.4px; height:7px;
                   border:solid #fff; border-width:0 2px 2px 0; transform:rotate(45deg); }

    /* ── De kaart per analysemonster ────────────────────────────────────── */
    .kaart { border:1px solid var(--bl-lijn); border-radius:6px; overflow:hidden;
             margin-bottom:24px; }
    .kaartkop { display:flex; align-items:center; justify-content:space-between; gap:12px;
                padding:7px 12px; background:var(--bl-groen); color:#fff; }
    .kaartnaam { font-size:0.95rem; font-weight:600; }
    .kaartnaam small { margin-right:8px; font-size:0.62rem; font-weight:400; opacity:.85;
                       letter-spacing:.08em; text-transform:uppercase; }
    .kaartkop .tag { padding:2px 9px; border-radius:999px; font-size:0.68rem;
                     background:rgba(255,255,255,.18); }
    .kaartmeta { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; padding:7px 12px;
                 background:var(--bl-rij); border-bottom:1px solid var(--bl-lijn); }
    .kaartmeta strong { display:block; margin-top:1px; font-weight:500; font-size:0.76rem;
                        overflow-wrap:anywhere; }
    .kaartanalyses { display:flex; flex-wrap:wrap; align-items:center; gap:5px; padding:7px 12px;
                     border-bottom:1px solid var(--bl-lijn); }
    .kaartanalyses .label { display:inline; margin-right:4px; }
    .chip { display:inline-flex; align-items:baseline; gap:6px; padding:1px 8px; border-radius:4px;
            border:1px solid var(--bl-groen-lijn); background:var(--bl-groen-zacht);
            font-size:0.74rem; }
    .chip em { font-style:normal; font-size:0.68rem; font-weight:600; color:var(--bl-groen-donker); }
    .chip.leeg { border-color:var(--bl-lijn); background:#f7f7f7; color:var(--bl-gedempt);
                 font-style:italic; }
    .kaart .doctabel th, .kaart .doctabel td { padding-left:12px; }
    .kaartopmerking { padding:8px 12px 10px; border-top:1px solid var(--bl-lijn);
                      background:var(--bl-rij); }
    .kaartopmerking p { margin:3px 0 0; font-size:0.8rem; line-height:1.5; }
    .kaart tbody tr:nth-child(even) td { background:var(--bl-rij); }

    @media (max-width: 760px) {
      .blad { padding:18px 14px; }
      .kerngegevens, .gegevens, .kaartmeta { grid-template-columns:1fr; }
      .kerngegevens > div + div { padding-left:0; border-left:none;
                                  border-top:1px solid var(--bl-lijn); }
      .blad table { font-size:0.72rem; }
      .blad th, .blad td { padding:4px 6px; }
      .matrixvak { overflow-x:auto; }
    }

    .muted { color:var(--mv-text-muted); padding:2rem; }
    .error { margin:0 0 0.875rem; padding:0.5rem 0.75rem; border-radius:var(--mv-radius-sm); background:var(--mv-danger-bg); color:var(--mv-danger); font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); }
    .empty { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:320px; text-align:center; border:1px dashed var(--mv-border); border-radius:8px; background:var(--mv-surface); padding:2rem; }
    .empty-icon { color:var(--mv-primary); margin-bottom:0.8rem; }
    .empty h2 { margin:0 0 0.5rem; color:var(--mv-text); font-size:1.2rem; }
    .empty p { max-width:520px; margin:0 0 1.25rem; color:var(--mv-text-sub); line-height:1.55; }
    /* Knop op dezelfde maat als 'Nieuwe boring' in de boringentab: vaste hoogte van 36px met
       horizontale padding, niet verticale padding rond een geerfde tekstgrootte. Die erfde
       hier 15px en werd daarmee zichtbaar hoger en breder dan dezelfde knop een tab verderop. */
    .btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; height:36px;
      border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
      border-radius:var(--mv-radius-sm); padding:0 14px; font-family:inherit;
      font-size:var(--mv-text-base); font-weight:var(--mv-weight-semi); cursor:pointer; text-decoration:none;
      white-space:nowrap; transition:background 0.15s ease, border-color 0.15s ease; }
    .btn.primary { background:var(--mv-primary); border-color:var(--mv-primary); color:#fff; }
    .btn.primary:hover:not(:disabled) { background:var(--mv-primary-hover); border-color:var(--mv-primary-hover); }
    .btn:hover:not(:disabled) { border-color:var(--mv-text-sub); }
    .btn:disabled { opacity:.55; cursor:not-allowed; }

    /* Melding: een regel, geen balk. Hij stond op geerfde 15px vet in een blok met 12px
       padding over de volle breedte, en dat is veel gewicht voor een mededeling die je
       hooguit even wilt zien. Nu valt hij op door kleur en niet door formaat. De kleuren
       lopen via tokens, want hardgecodeerd geel-op-bruin klopt niet in het donkere thema. */
    /* Aanmaakpopup. Bewust smal: drie keuzes, meer niet. */
    .dialog-overlay { position:fixed; inset:0; z-index:var(--mv-z-modal); background:rgba(16,28,18,0.55);
      display:flex; align-items:flex-start; justify-content:center; padding:2.75rem 1.25rem; overflow:auto; }
    .dialog { width:100%; max-width:26rem; background:var(--mv-surface); border-radius:var(--mv-radius-xl);
      box-shadow:0 30px 80px rgba(0,0,0,0.35); overflow:hidden; }
    .dialog-head { display:flex; align-items:center; justify-content:space-between; gap:1rem;
      padding:1.125rem 1.25rem; border-bottom:1px solid var(--mv-border-soft); }
    .dialog-head h3 { margin:0; font-size:var(--mv-text-lg); font-weight:var(--mv-weight-heavy);
      letter-spacing:var(--mv-tracking-snug); color:var(--mv-text); }
    .dialog-body { padding:1.25rem; display:flex; flex-direction:column; gap:0.875rem; }
    .dialog-body label { display:flex; flex-direction:column; gap:0.3rem; }
    .dialog-body label > span { font-size:var(--mv-text-xs); font-weight:var(--mv-weight-heavy);
      letter-spacing:var(--mv-tracking-caps); text-transform:uppercase; color:var(--mv-text-sub); }
    .dialog-body select:disabled { color:var(--mv-text-sub); background:var(--mv-surface-alt); cursor:default; }
    .field-hint { color:var(--mv-text-muted); font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); line-height:1.45; }
    .field-hint a { color:var(--mv-primary); text-decoration:underline; }
    /* Elke groep een eigen tegel op een zacht vlak, in plaats van gescheiden door een streep.
       Wit-op-wit las slecht: de invoervelden zijn zelf wit, dus op een witte ondergrond zag je
       alleen dunne randjes en liep alles in elkaar over. Dezelfde oplossing als bij de lagen in
       een opname. De velden houden hun witte achtergrond (zie de veldregel verderop) en steken
       nu af tegen de tegel. */
    .group { padding:0.7rem 0.9rem 0.8rem; background:var(--mv-surface-alt); border:1px solid var(--mv-border); border-radius:10px; }
    .group h3 { margin:0 0 0.55rem; }
    /* auto-fill en niet auto-fit: lege kolommen blijven bestaan, dus de twee datums zijn even
       breed als de velden erboven in plaats van samen de hele regel te vullen. */
    .form-grid.tweede-regel { margin-top:0.7rem; grid-template-columns:repeat(auto-fill,minmax(215px,1fr)); }
    .readonly { display:flex; flex-direction:column; gap:0.25rem; }
    .readonly span { font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); }
    /* Zelfde hoogte als een invoerveld ernaast, anders staat de rij scheef. */
    /* gap: het logovierkantje bij Laboratorium staat anders tegen de naam aan. */
    .readonly strong { display:flex; align-items:center; gap:0.5rem; min-height:2.125rem; font-size:var(--mv-text-md); font-weight:var(--mv-weight-bold); color:var(--mv-text); }
    .matrix-empty { margin:0; padding:1.5rem; text-align:center; color:var(--mv-text-sub); font-size:var(--mv-text-sm); }
    .matrix th .col-name { display:block; max-width:11rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .matrix th code { display:block; margin-top:0.125rem; font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); }
    .picker { max-width:34rem; }
    .picker-sub { margin:0.125rem 0 0; font-size:var(--mv-text-xs); font-weight:var(--mv-weight-heavy); letter-spacing:var(--mv-tracking-caps); text-transform:uppercase; color:var(--mv-text-sub); }
    .picker-input { width:100%; box-sizing:border-box; padding:0.5rem 0.6875rem; border:1.5px solid var(--mv-border); border-radius:var(--mv-radius); font:inherit; font-size:var(--mv-text-md); color:var(--mv-text); background:var(--mv-surface); }
    .picker-input:focus { outline:none; border-color:var(--mv-primary); }
    .picker-error { margin:0.6875rem 0 0; font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); color:var(--mv-danger); }
    .picker-note { margin:1.25rem 0; text-align:center; font-size:var(--mv-text-sm); color:var(--mv-text-sub); }
    /* Vaste hoogte: de lijst mag het venster niet laten springen bij elke toetsaanslag. */
    .picker-results { margin-top:0.6875rem; max-height:19rem; overflow:auto; }
    .picker-row { display:flex; align-items:baseline; justify-content:space-between; gap:0.75rem; width:100%; padding:0.5rem 0.625rem; border:none; border-radius:var(--mv-radius-sm); background:none; text-align:left; font:inherit; cursor:pointer; }
    .picker-row:hover { background:var(--mv-surface-alt); }
    .picker-name { font-size:var(--mv-text-md); font-weight:var(--mv-weight-bold); color:var(--mv-text); }
    .picker-meta { display:flex; align-items:baseline; gap:0.5rem; flex:none; font-size:var(--mv-text-xs); color:var(--mv-text-sub); }
    .picker-meta code { font-weight:var(--mv-weight-heavy); }
    .dialog-note { margin:0; font-size:var(--mv-text-sm); color:var(--mv-warning-text); }
    .dialog-actions { display:flex; justify-content:flex-end; gap:0.5rem;
      padding:0 1.25rem 1.25rem; }
    .icon-btn { width:2rem; height:2rem; border:none; background:none; border-radius:var(--mv-radius-sm);
      display:flex; align-items:center; justify-content:center; color:var(--mv-text-sub); cursor:pointer; }
    .icon-btn:hover { background:var(--mv-surface-alt); }

    .notice { display:inline-flex; align-items:center; gap:7px; padding:0.4rem 0.7rem;
      border-radius:var(--mv-radius-sm); border:1px solid var(--mv-warning-panel-border);
      background:var(--mv-warning-panel-bg); color:var(--mv-warning-text);
      font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); margin-bottom:0.875rem; }
    .notice svg { flex:none; }
    .overview-head { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:1rem; }
    .metrics { display:flex; gap:0.75rem; flex-wrap:wrap; }
    .metric { min-width:118px; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); padding:0.7rem 0.9rem; background:var(--mv-surface); }
    /* Getal en label allebei op het zwaarste gewicht maakte er vier zwarte blokjes van. Het
       getal is de inhoud en mag opvallen, het label hoort te wijken: lichter, kleiner en in
       de gedempte kleur. Alleen het getal draagt nu gewicht. */
    .metric strong { display:block; font-size:var(--mv-text-xl); font-weight:var(--mv-weight-semi); letter-spacing:var(--mv-tracking-snug); color:var(--mv-text); line-height:1.2; }
    .metric span { display:block; margin-top:2px; color:var(--mv-text-muted); font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); letter-spacing:0.02em; }
    .toolbar { display:flex; align-items:center; gap:1rem; margin-bottom:1rem; }
    /* Statuskiezer als smalle dropdown naast de zoekbalk, zelfde vorm als op /projects. Hij
       erfde hierboven de width:100% van de gedeelde veldregel en werd daardoor even breed als
       het zoekveld, terwijl hij maar een handvol korte waarden bevat. */
    .status-group { display:flex; align-items:center; gap:0.5rem; flex-shrink:0; }
    .status-group > span { font-size:var(--mv-text-base); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); white-space:nowrap; }
    .toolbar select { width:auto; min-width:130px; padding:0 2rem 0 0.75rem; cursor:pointer;
      font-weight:var(--mv-weight-semi); appearance:none; background-repeat:no-repeat;
      /* Zelfde afstand als de formuliervelden hieronder en als .mv-select in base.css. */
      background-position:right 0.75rem center; background-size:1rem;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B978D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E"); }
    .search { flex:1; display:flex; align-items:center; gap:8px; border:1px solid var(--mv-border); border-radius:8px; padding:0 0.75rem; background:var(--mv-surface); color:var(--mv-text-muted); }
    .search input, .toolbar select, label input, label select, textarea { width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); padding:0 0.7rem; height:36px; background:var(--mv-surface); color:var(--mv-text); font-family:inherit; font-size:var(--mv-text-base); }
    textarea { height:auto; padding:0.55rem 0.7rem; }
    /* Dropdowns in de formulieren krijgen dezelfde chevron als de rest van de backoffice; zie
       .mv-select in base.css. Zonder deze regel tekent de browser zijn eigen pijltje, strak
       tegen de rechterrand -- en in een brede tegel valt dat ver van de waarde af te staan. */
    label.leeg input, label.leeg select { border-color:#fca5a5; }
    label.leeg searchable-select::part(trigger) { border-color:#fca5a5; }
    label select { appearance:none; -webkit-appearance:none; padding-right:2rem;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B978D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat:no-repeat; background-position:right 0.75rem center; background-size:1rem; }
    .search { height:36px; }
    .search input { border:none; padding:0; height:100%; outline:none; }
    .table { border:1px solid var(--mv-border); border-radius:var(--mv-radius-lg); overflow:hidden; background:var(--mv-surface); }
    .row { display:grid; grid-template-columns:110px 110px minmax(120px,150px) 100px 110px 100px 110px minmax(180px,1fr) 70px 100px; gap:10px; align-items:center; padding:0.6875rem 0.9rem; border-bottom:1px solid var(--mv-border-soft); text-align:left; color:var(--mv-text); font-size:var(--mv-text-base); }
    .row.head { background:var(--mv-surface-alt); font-size:0.76rem; font-weight:800; color:var(--mv-text-sub); }
    button.row { width:100%; border-width:0 0 1px; background:var(--mv-surface); cursor:pointer; font-family:inherit; font-size:var(--mv-text-base); }
    button.row:hover { background:var(--mv-surface-alt); }
    .row .project { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .pill { display:inline-flex; border-radius:999px; padding:2px 8px; font-size:0.74rem; font-weight:800; }
    .pill.ok { background:var(--mv-primary-tint); color:var(--mv-primary); }
    .pill.warn { background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); }
    .pill.bad { background:var(--mv-danger-bg,#fff5f5); color:var(--mv-danger,#dc3545); }
    .badges { display:flex; flex-wrap:wrap; gap:4px; }
    .badges em { font-style:normal; background:var(--mv-primary-tint); color:var(--mv-primary); border-radius:5px; padding:2px 6px; font-size:0.75rem; font-weight:800; }
    .badges small, .empty-line { color:var(--mv-text-muted); }
    .empty-line { padding:2rem; text-align:center; }
    .detail { display:flex; flex-direction:column; }
    .detail-head { display:flex; align-items:center; gap:0.8rem; margin-bottom:1rem; }
    .detail-head h2 { margin:0; font-size:1.2rem; color:var(--mv-text); }
    .detail-head p { margin:0.15rem 0 0; color:var(--mv-text-sub); font-size:0.85rem; }
    .head-actions { margin-left:auto; display:flex; gap:0.5rem; }
    .icon-btn { width:34px; height:34px; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text); border-radius:8px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
    .tabs { display:flex; gap:0; border-bottom:1px solid var(--mv-border); margin-bottom:1rem; }
    .tabs button { border:none; background:none; color:var(--mv-text-sub); padding:0.75rem 1rem; font-weight:800; cursor:pointer; border-bottom:2px solid transparent; }
    .tabs button.active { color:var(--mv-primary); border-bottom-color:var(--mv-primary); }
    /* Houder van de tegels: zelf wit, met ruimte ertussen zodat de groepen los van elkaar
       leesbaar blijven in plaats van als één blok. */
    .panel { display:flex; flex-direction:column; gap:0.7rem; border:1px solid var(--mv-border); border-radius:8px; padding:0.9rem; background:var(--mv-surface); }
    /* Automatisch zoveel kolommen als er passen. Met drie vaste kolommen bleef er een halve
       rij leeg zodra een groep er vier had, en paste het formulier niet op één scherm. */
    .form-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(215px,1fr)); gap:0.7rem 1rem; }
    /* Twee velden in één roostercel, onder elkaar. Nodig voor het e-mailadres van de
       projectleider: als eigen cel schoof dat naar de volgende rij, helemaal links, en dan
       stond het los van het veld waar het bij hoort. */
    .stack { display:flex; flex-direction:column; gap:0.75rem; }
    /* Eigen element: erft de regel voor input/select hierboven niet, dus hier op dezelfde
       hoogte zetten als de velden ernaast. */
    .form-grid searchable-select { display:block; --ss-text-transform:none; }
    .form-grid searchable-select::part(trigger) {
      height:36px; padding:0 0.7rem; border-radius:var(--mv-radius-sm); font-size:var(--mv-text-base);
    }
    label span, .wide-label { display:block; margin-bottom:0.35rem; color:var(--mv-text-sub); font-size:0.78rem; font-weight:800; }
    /* Twee regels hoog: de opmerking is zelden lang, en een leeg vak van 120 px duwde de
       opslagknop van het scherm af. Groter slepen kan nog steeds. */
    textarea { min-height:86px; height:auto; padding-top:0.45rem; padding-bottom:0.45rem; resize:vertical; }
    .actions { display:flex; align-items:center; justify-content:flex-end; gap:0.75rem; margin-top:1rem; }
    .actions.vast { position:sticky; bottom:0; margin-top:0.7rem; padding:0.7rem 0 0.9rem;
                    background:var(--mv-bg); z-index:2; }
    .nogin { margin-right:auto; font-size:0.76rem; font-weight:700; color:var(--mv-warning-text, #8c6512); }
    .actions.split { justify-content:flex-start; flex-wrap:wrap; }
    .composition { display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:1rem; }
    .volle-breedte { grid-column:1 / -1; }
    .profile-area, .side-box, .matrix { border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); }
    .profile-toolbar, .side-title, .matrix-head { display:flex; align-items:center; justify-content:space-between; gap:8px; padding:0.75rem 0.9rem; border-bottom:1px solid var(--mv-border); color:var(--mv-text); font-weight:800; }
    .profile-toolbar span { color:var(--mv-text-sub); font-size:0.8rem; }
    .profiles { display:flex; gap:0.75rem; overflow-x:auto; padding:1rem; align-items:flex-start; min-height:280px; }
    .boring-col { width:116px; flex:0 0 116px; }
    .boring-title { font-size:0.8rem; font-weight:900; color:var(--mv-text); margin-bottom:0.5rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .layers { position:relative; border-left:2px solid var(--mv-border); background:var(--mv-surface-alt); border-radius:6px; overflow:hidden; }
    .layer { position:absolute; left:7px; right:0; border:1px solid rgba(0,0,0,0.12); border-radius:5px; padding:4px; display:flex; flex-direction:column; align-items:flex-start; justify-content:center; color:#17201c; cursor:pointer; overflow:hidden; font:inherit; text-align:left; }
    .layer.selected { outline:3px solid var(--mv-primary); z-index:3; }
    .layer span { font-size:0.68rem; font-weight:900; line-height:1.1; }
    .layer small { font-size:0.65rem; font-weight:700; opacity:.8; }
    .sample-side { display:flex; flex-direction:column; gap:1rem; min-width:0; }
    .side-box { padding-bottom:0.75rem; }
    .side-box p { margin:0.75rem 0.9rem 0; color:var(--mv-text-sub); font-size:0.88rem; line-height:1.45; }
    .sample-card { display:flex; align-items:center; justify-content:space-between; gap:8px; margin:0.7rem 0.75rem 0; padding:0.7rem; border:1px solid var(--mv-border); border-radius:8px; }
    .sample-card strong { display:block; color:var(--mv-text); }
    .sample-card span { color:var(--mv-text-sub); font-size:0.78rem; }
    .sample-card button { border:none; background:none; color:var(--mv-danger,#dc3545); cursor:pointer; display:flex; }
    .matrix { grid-column:1 / -1; overflow:auto; }
    table { width:100%; border-collapse:collapse; }
    th, td { border-top:1px solid var(--mv-border); padding:0.65rem 0.8rem; color:var(--mv-text); text-align:left; }
    th { background:var(--mv-surface-alt); color:var(--mv-text-sub); font-size:0.78rem; }
    .check { min-width:58px; border:1px solid var(--mv-border); border-radius:7px; background:var(--mv-surface); color:var(--mv-text-sub); padding:0.35rem 0.55rem; cursor:pointer; font-weight:800; }
    .check.on { background:var(--mv-primary-tint); color:var(--mv-primary); border-color:var(--mv-primary); }
    @media (max-width: 1000px) {
      .adminvlak { grid-template-columns:minmax(0,1fr); }
      .checklist { position:static; }
    }
    @media (max-width: 640px) {
      .opdrachtkop { padding:0; }
      .admkaart { padding:0 16px 16px; }
      .admkop { margin:0 -16px 16px; padding:9px 16px; }
      .opdrachtkop .tabs { gap:16px; overflow-x:auto; }
      .opdrachtkop .tabs button { white-space:nowrap; }
    }
    @media (max-width: 980px) {
      .composition { grid-template-columns:1fr; }
      .form-grid { grid-template-columns:1fr; }
      .row { grid-template-columns:1fr; }
      .row.head { display:none; }
      .head-actions, .overview-head, .toolbar { flex-direction:column; align-items:stretch; }
    }
  `;
}

customElements.define('lab-order-page', LabOrderPage);
